// Per-user cache namespacing. Set after profile loads (see initializeProfileId
// in component body). Default to 'default' until then so cache writes don't
// crash, but the keys only stabilize once we know the real user id.
let _profUserId = 'default';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Alert, ActivityIndicator, Animated, Dimensions, Modal, TextInput, Image,
  RefreshControl, FlatList, Platform, Share, Linking, KeyboardAvoidingView,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons, FontAwesome5, MaterialCommunityIcons, Feather } from '@expo/vector-icons';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

let AsyncStorage;
try { AsyncStorage = require('@react-native-async-storage/async-storage').default; }
catch { AsyncStorage = { getItem:async()=>null, setItem:async()=>null, removeItem:async()=>null, multiRemove:async()=>null }; }

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

// ═══════════════════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED KEYS / HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
// ── Shared keys – must match ProfileScreen, Login, Signup exactly ─────────────
const PROF_KEYS = {
  PROFILE: 'prof_user_profile',
  DASH:    'prof_dashboard_data',
};

const SHARED = {
  PORTAL_APPS : 'intern_portal_applications',
  PROGRAMS    : 'intern_programs',
  SAVED       : 'studentData',
};

const CK = (key) => `prof_${_profUserId}_${key}`; // cache key builder

const fmtDate   = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); } catch { return d||'N/A'; } };
const fmtTime   = d => { try { return new Date(d).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'}); } catch { return ''; } };
const fmtSalary = (min,max,cur='GBP') => { const s=cur==='GBP'?'£':'$'; if(min&&max) return `${s}${(min/1000).toFixed(0)}k–${s}${(max/1000).toFixed(0)}k`; if(min) return `From ${s}${(min/1000).toFixed(0)}k`; if(max) return `Up to ${s}${(max/1000).toFixed(0)}k`; return 'Competitive'; };
// Coerce a single skill (string OR object) to a display string — prevents
// "Objects are not valid as a React child" crashes when rendering skill chips.
const skillLabel = (s) => (typeof s === 'string' ? s : (s?.name || s?.title || s?.skill_name || s?.skill || ''));
// Coerce any skills value (array of strings/objects, or comma string) to a clean string array.
const toSkillStrings = (v) => {
  let raw = v ?? [];
  if (typeof raw === 'string') raw = raw.split(',');
  if (!Array.isArray(raw)) return [];
  return raw.map(skillLabel).map(s => String(s).trim()).filter(Boolean);
};
const greeting  = () => { const h=new Date().getHours(); return h<12?'Good morning':h<17?'Good afternoon':'Good evening'; };
const daysBetween = (a,b) => Math.ceil((new Date(a)-new Date(b))/(1000*60*60*24));
const truncate = (s,n=40) => s?.length>n ? s.slice(0,n)+'…' : s||'';

const STATUS_COLORS = {
  active:{bg:'#dcfce7',text:'#16a34a',label:'Active'},
  pending:{bg:'#fef3c7',text:'#d97706',label:'Pending'},
  accepted:{bg:'#dcfce7',text:'#16a34a',label:'Accepted'},
  rejected:{bg:'#fee2e2',text:'#dc2626',label:'Rejected'},
  withdrawn:{bg:'#f1f5f9',text:'#64748b',label:'Withdrawn'},
  scheduled:{bg:'#dbeafe',text:'#2563eb',label:'Scheduled'},
  completed:{bg:'#dcfce7',text:'#16a34a',label:'Completed'},
  cancelled:{bg:'#fee2e2',text:'#dc2626',label:'Cancelled'},
  Submitted:{bg:'#ede9fe',text:'#7c3aed',label:'Submitted'},
  'Under Review':{bg:'#fef3c7',text:'#d97706',label:'Under Review'},
  'Interview Scheduled':{bg:'#dbeafe',text:'#2563eb',label:'Interview Scheduled'},
  Accepted:{bg:'#dcfce7',text:'#16a34a',label:'Accepted'},
  Rejected:{bg:'#fee2e2',text:'#dc2626',label:'Rejected'},
  offered:{bg:'#ecfdf5',text:'#059669',label:'Offered'},
  hired:{bg:'#dcfce7',text:'#059669',label:'Hired'},
};
const scBadge = s => STATUS_COLORS[s] || {bg:'#f1f5f9',text:'#64748b',label:s||'Unknown'};

const STAGE_COLORS = {
  applicants:'#667eea',screening:'#4facfe',shortlisted:'#43e97b',assessment:'#f093fb',
  video_interview:'#ffecd2',final_interview:'#ff6b6b',offered:'#4ecdc4',
  accepted:'#10b981',hired:'#059669',rejected:'#ef4444',
};
const scStage = s => STAGE_COLORS[s]||'#64748B';

// ═══════════════════════════════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════════════════════════════
const T = {
  primary:'#0e4e96', primaryLight:'#1e7be2', primaryBg:'#EFF6FF',
  success:'#10b981', successBg:'#dcfce7',
  warning:'#f59e0b', warningBg:'#fef3c7',
  danger:'#ef4444',  dangerBg:'#fee2e2',
  purple:'#667eea',  purpleBg:'#ede9fe',
  pink:'#ec4899',    pinkBg:'#fdf2f8',
  text:'#1F2937', textSec:'#64748B', textMut:'#94a3b8',
  bg:'#F0F4F8', card:'#FFF', border:'#E2E8F0', borderLight:'#F1F5F9',
  white:'#FFF',
};

const SHADOW = { shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.08, shadowRadius:8, elevation:3 };
const SHADOW_SM = { shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:4, elevation:2 };

// ═══════════════════════════════════════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════
const StatusBadge = ({ status }) => {
  const b = scBadge(status);
  return (
    <View style={[st.statusBadge,{backgroundColor:b.bg}]}>
      <Text style={[st.statusBadgeT,{color:b.text}]}>{b.label}</Text>
    </View>
  );
};

const AnimatedBar = ({ progress, color = T.primary, height = 6 }) => {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, { toValue: Math.min(progress,100), duration: 900, useNativeDriver: false }).start();
  }, [progress]);
  const w = anim.interpolate({ inputRange:[0,100], outputRange:['0%','100%'], extrapolate:'clamp' });
  return (
    <View style={{height, backgroundColor:T.borderLight, borderRadius:height/2, overflow:'hidden'}}>
      <Animated.View style={{height, borderRadius:height/2, backgroundColor:color, width:w}} />
    </View>
  );
};

const SectionHdr = ({ icon, title, actionLabel, onAction, iconColor='#333' }) => (
  <View style={st.sectionHdr}>
    <MaterialCommunityIcons name={icon} size={20} color={iconColor}/>
    <Text style={st.sectionTitle}>{title}</Text>
    {actionLabel && (
      <TouchableOpacity onPress={onAction} style={st.seeAll}>
        <Text style={st.seeAllT}>{actionLabel}</Text>
        <Ionicons name="chevron-forward" size={14} color={T.primary}/>
      </TouchableOpacity>
    )}
  </View>
);

const EmptyBlock = ({ icon='folder-open-outline', title, subtitle }) => (
  <View style={{alignItems:'center',paddingVertical:30,paddingHorizontal:20,backgroundColor:T.borderLight,borderRadius:14,marginHorizontal:16,marginBottom:12}}>
    <MaterialCommunityIcons name={icon} size={44} color={T.textMut}/>
    <Text style={{fontSize:14,fontWeight:'600',color:T.textSec,marginTop:10}}>{title}</Text>
    {subtitle&&<Text style={{fontSize:12,color:T.textMut,marginTop:3,textAlign:'center'}}>{subtitle}</Text>}
  </View>
);

const CountBadge = ({ count, color=T.danger }) => {
  if (!count || count<=0) return null;
  return (
    <View style={[st.notifBadge,{backgroundColor:color}]}>
      <Text style={st.notifBadgeT}>{count>99?'99+':count}</Text>
    </View>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// USER PHOTO — handles http, file://, content://, data:image (base64)
// Uses same field names as ProfileScreen (profileImage, profileImageB64, etc.)
// ═══════════════════════════════════════════════════════════════════════════════
const UserPhoto = ({ profile, size = 70, initials = 'U' }) => {
  const [imgError, setImgError] = React.useState(false);
  const [retryKey, setRetryKey] = React.useState(0);

  // Resolve the best available image URI from profile data
  // Priority: profileImageB64 > profile_photo_b64 > profileImage > all other fields
  const uri = React.useMemo(() => {
    if (!profile || typeof profile !== 'object') return null;

    // 1. Check base64 first (most reliable — survives app restarts)
    const b64Fields = ['profileImageB64','profile_image_b64','avatarB64','photoB64'];
    for (const k of b64Fields) {
      const v = profile[k];
      if (v && typeof v === 'string' && v.length > 100) {
        // Ensure it has the data: prefix
        return v.startsWith('data:') ? v : `data:image/jpeg;base64,${v}`;
      }
    }

    // 2. Check all possible URI fields
    const uriFields = [
      'profileImage','profile_image','profilePhoto','profile_photo',
      'avatar','avatar_url','avatarUrl','avatarURL',
      'photo','photo_url','photoUrl','photoURL',
      'image','image_url','imageUrl','imageURL',
      'picture','picture_url','pictureUrl',
      'headshot','thumbnail','user_image','userImage',
      'display_photo','displayPhoto','pic','img',
    ];
    for (const k of uriFields) {
      const v = profile[k];
      if (v && typeof v === 'string' && v.length > 5) {
        // Accept http, https, file://, content://, data: URIs
        if (v.startsWith('http') || v.startsWith('file://') ||
            v.startsWith('content://') || v.startsWith('data:') ||
            v.length > 200) {  // long strings are likely base64 without prefix
          return v.length > 200 && !v.startsWith('data:') && !v.startsWith('http')
            ? `data:image/jpeg;base64,${v}` : v;
        }
      }
    }
    return null;
  }, [profile, retryKey]);

  const showImage = uri && !imgError;

  return (
    <View style={{
      width: size, height: size, borderRadius: size / 2,
      backgroundColor: showImage ? T.border : T.primaryBg,
      justifyContent: 'center', alignItems: 'center',
      borderWidth: 3, borderColor: T.primary,
      overflow: 'hidden',
    }}>
      {showImage ? (
        <Image
          key={`photo_${retryKey}`}
          source={{ uri }}
          style={{ width: size, height: size }}
          resizeMode="cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <Text style={{ fontSize: Math.round(size * 0.36), fontWeight: '800', color: T.primary }}>
          {initials}
        </Text>
      )}
    </View>
  );
};
const TILE_GAP = 10;
const TILE_COLS = 3;
const TILE_PADDING = 16;
const TILE_W = (SCREEN_W - TILE_PADDING*2 - TILE_GAP*(TILE_COLS-1)) / TILE_COLS;

const DashboardTile = ({ label, icon, bg, screen, premium=false, navigation:navProp }) => {
  const nav = useNavigation();
  return (
    <TouchableOpacity
      style={[st.tile, {backgroundColor: bg||T.primary, width: TILE_W}]}
      onPress={() => {
        try { (navProp||nav).navigate(screen); }
        catch { Alert.alert('Coming Soon',`${label} will be available soon!`); }
      }}
      activeOpacity={0.82}>
      <View style={st.tileContent}>
        <View style={st.tileIconWrap}>
          <FontAwesome5 name={icon} size={22} color={T.white}/>
          {premium&&<View style={st.lockOverlay}><MaterialCommunityIcons name="crown" size={10} color="#fbbf24"/></View>}
        </View>
        <Text style={st.tileLabel} numberOfLines={2}>{label}</Text>
      </View>
    </TouchableOpacity>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function ProfessionalDashboard() {
  const navigation = useNavigation();

  // ── User state ──────────────────────────────────────────────────────────────
  const [userProfile,    setUserProfile]    = useState(null);
  const [isLoading,      setIsLoading]      = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [errorMessage,   setErrorMessage]   = useState(null);
  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef(null);

  // ── Dashboard data ─────────────────────────────────────────────────────────
  const [dashStats, setDashStats] = useState({
    total_applications:0, active_applications:0, interviews_scheduled:0,
    interviews_completed:0, offers_received:0, offers_pending:0,
    profile_views:0, jobs_saved:0, notifications_count:0, profile_completion:0,
    success_rate:0, portal_applications:0, portal_saved:0,
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [upcomingInterviews,  setUpcomingInterviews]  = useState([]);
  const [recommendedJobs,     setRecommendedJobs]     = useState([]);
  const [activeOffers,        setActiveOffers]        = useState([]);
  const [notifications,       setNotifications]       = useState([]);
  const [activityFeed,        setActivityFeed]        = useState([]);
  const [savedJobs,           setSavedJobs]           = useState([]);

  // ── Internship portal data ─────────────────────────────────────────────────
  const [portalApps,   setPortalApps]   = useState([]);
  const [portalSaved,  setPortalSaved]  = useState([]);
  const [publishedPrograms, setPublishedPrograms] = useState([]);

  // ── Skills & analytics ─────────────────────────────────────────────────────
  const [skillsData,   setSkillsData]   = useState([]);
  const [careerScore,  setCareerScore]  = useState(0);

  // ── Profile Screen sync (completion %, strength, photo, etc.) ──────────────
  const [profileScreenUser,  setProfileScreenUser]  = useState(null);   // from PROF_KEYS.DASH
  // Job title shown on the welcome card. Read DIRECTLY from PROF_KEYS on
  // every focus, bypassing ProfileManagementService entirely (whose
  // AsyncStorage cache holds a stale value like "Senior Full-Stack
  // Engineer" that never matches what the user just typed in ProfileScreen).
  // Single source of truth — no merging, no fallbacks to other services.
  const [profileTitle, setProfileTitle] = useState('');
  const [profileCompletion,  setProfileCompletion]  = useState(0);
  const [profileStrength,    setProfileStrength]    = useState(0);
  const [expYears,           setExpYears]           = useState(0);   // computed from real work-experience entries

  // ── New widget data: AI matches, daily brief, goals, countdown ────────────
  const [aiMatches,    setAiMatches]    = useState([]);          // top 5 personalised job matches with scores
  const [dailyBrief,   setDailyBrief]   = useState('');          // AI-generated morning summary
  const [briefLoading, setBriefLoading] = useState(false);
  const [careerGoals,  setCareerGoals]  = useState({              // weekly goal tracking
    appsThisWeek: 0, appsTarget: 5,
    interviewsThisWeek: 0, interviewsTarget: 1,
    streakDays: 0,
  });
  const [profileChecklist, setProfileChecklist] = useState([]);   // actionable completion items

  // ── AI chat ────────────────────────────────────────────────────────────────
  const [showAIChat,   setShowAIChat]   = useState(false);
  const [aiMessages,   setAiMessages]   = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isAITyping,   setIsAITyping]   = useState(false);
  const chatScrollRef  = useRef(null);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [showNotifModal,  setShowNotifModal]  = useState(false);
  const [showAnalytics,   setShowAnalytics]   = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchResults,   setSearchResults]   = useState([]);
  const [searching,       setSearching]       = useState(false);
  const [activeTab,       setActiveTab]       = useState('overview');
  const [showShareModal,  setShowShareModal]  = useState(false);

  // ── Computed ───────────────────────────────────────────────────────────────
  const unread   = useMemo(() => notifications.filter(n=>!n.is_read).length, [notifications]);
  // Prefer profile screen user data (most accurate), fall back to API profile
  const name     = profileScreenUser?.fullName || userProfile?.full_name || userProfile?.name || 'Professional';
  const initials = useMemo(() => name.split(' ').map(w=>w?.[0]||'').join('').slice(0,2).toUpperCase(), [name]);
  // Use ProfileScreen's real completion %, fall back to dashStats
  const profComp = profileCompletion || dashStats.profile_completion || 0;

  // Merged profile object for UserPhoto — combines API profile + ProfileScreen fields
  const mergedPhotoProfile = useMemo(() => ({
    ...(userProfile || {}),
    ...(profileScreenUser || {}),
  }), [userProfile, profileScreenUser]);

  // ═════════════════════════════════════════════════════════════════════════════
  // API FUNCTIONS
  // ═════════════════════════════════════════════════════════════════════════════
  const fetchUserProfile = useCallback(async () => {
    try {
      // Primary: unified service (backend-first → cache fallback inside)
      try {
        await ProfileManagementService.bootstrap();
        const svc = await ProfileManagementService.getProfile();
        if (svc.success && svc.data) {
          // Flatten the unified shape into the DB-style shape the dashboard expects
          const sd = svc.data;
          const pi = sd.personalInfo || sd;
          const flat = {
            ...sd,
            full_name: pi.fullName || pi.name || pi.full_name,
            name: pi.fullName || pi.name,
            email: pi.email,
            phone: pi.phone,
            location: pi.location,
            job_title: sd.profession || pi.jobTitle || pi.job_title,
            profile_image: pi.profileImage || pi.profile_image,
          };
          setUserProfile(flat);
          await AsyncStorage.setItem(CK('profile'), JSON.stringify(flat));
          // Stash email + user_id under stable global keys so the candidate-scoped
          // endpoints can find them even on the very first load before React
          // state has propagated, or after an app restart.
          if (flat.email)    AsyncStorage.setItem('user_email', flat.email).catch(()=>{});
          if (flat.id)       AsyncStorage.setItem('user_id', String(flat.id)).catch(()=>{});
          else if (flat.user_id) AsyncStorage.setItem('user_id', String(flat.user_id)).catch(()=>{});
          return flat;
        }
      } catch (_) {}

      const cached = await AsyncStorage.getItem(CK('profile'));
      if (cached) { const p=JSON.parse(cached); setUserProfile(p); return p; }
      const email = await AsyncStorage.getItem('user_email');
      const uid   = await AsyncStorage.getItem('user_id') || await AsyncStorage.getItem('userId');
      let r;
      if (email)    r = await apiFetch(`/api/data/customers?email=${encodeURIComponent(email)}&limit=1`);
      else if (uid) r = await apiFetch(`/api/data/customers?user_id=${uid}&limit=1`);
      else          r = await apiFetch('/api/data/customers?limit=1&sort=created_at&order=desc');

      if (!r.error && Array.isArray(r) && r.length>0) {
        setUserProfile(r[0]);
        await AsyncStorage.setItem(CK('profile'), JSON.stringify(r[0]));
        return r[0];
      }
      const authUser = await AsyncStorage.getItem('user_data');
      if (authUser) { const p=JSON.parse(authUser); setUserProfile(p); await AsyncStorage.setItem(CK('profile'),JSON.stringify(p)); return p; }
    } catch(e) { console.warn('fetchUserProfile:', e.message); }
    return null;
  }, []);

  const fetchApplications = useCallback(async (emailOverride) => {
    try {
      // Resolve the candidate's email from the most reliable source available.
      // Priority: explicit override (passed by loadAllData with fresh profile)
      // → React state → cached profile blob → standalone email keys → service.
      // This makes the call work even on the very first load, before state has
      // had a chance to re-render with the freshly fetched profile.
      let email = emailOverride
        || userProfile?.email
        || profileScreenUser?.email;
      if (!email) {
        try {
          const cached = await AsyncStorage.getItem(CK('profile'));
          if (cached) email = JSON.parse(cached)?.email;
        } catch {}
      }
      if (!email) {
        email = (await AsyncStorage.getItem('user_email'))
             || (await AsyncStorage.getItem('userEmail'));
      }
      if (!email) {
        try {
          const svc = await ProfileManagementService.getProfile();
          email = svc?.data?.email || svc?.data?.personalInfo?.email;
        } catch {}
      }

      const r = email
        ? await apiFetch(`/api/professional/my-applications?email=${encodeURIComponent(email)}&limit=20`)
        : await apiFetch('/api/data/applications?sort=applied_date&order=desc&limit=20');
      const rows = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : null;
      if (rows && !r.error) {
        setRecentApplications(rows);
        await AsyncStorage.setItem(CK('apps'), JSON.stringify(rows));
        return rows;
      }
      const cached = await AsyncStorage.getItem(CK('apps'));
      if (cached) { const p=JSON.parse(cached); setRecentApplications(p); return p; }
    } catch (e) { console.warn('fetchApplications:', e?.message); }
    return [];
  }, [userProfile, profileScreenUser]);

  const fetchInterviews = useCallback(async (emailOverride) => {
    try {
      let email = emailOverride
        || userProfile?.email
        || profileScreenUser?.email;
      if (!email) {
        try {
          const cached = await AsyncStorage.getItem(CK('profile'));
          if (cached) email = JSON.parse(cached)?.email;
        } catch {}
      }
      if (!email) {
        email = (await AsyncStorage.getItem('user_email'))
             || (await AsyncStorage.getItem('userEmail'));
      }
      if (!email) {
        try {
          const svc = await ProfileManagementService.getProfile();
          email = svc?.data?.email || svc?.data?.personalInfo?.email;
        } catch {}
      }

      const r = email
        ? await apiFetch(`/api/professional/my-interviews?email=${encodeURIComponent(email)}&limit=20`)
        : await apiFetch('/api/data/application_interviews?sort=scheduled_date&order=asc&limit=20');
      const rows = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : null;
      if (rows && !r.error) {
        const now = new Date();
        const dateOf = i => i.scheduled_at || i.scheduled_date || i.interview_date || i.scheduled_for;
        const upcoming = rows
          .filter(i => {
            const d = dateOf(i);
            return d && new Date(d) >= now && i.status !== 'completed' && i.status !== 'cancelled';
          })
          .sort((a,b) => new Date(dateOf(a)) - new Date(dateOf(b)));
        setUpcomingInterviews(upcoming.slice(0,5));
        await AsyncStorage.setItem(CK('interviews'), JSON.stringify(upcoming));
        return rows;
      }
      const cached = await AsyncStorage.getItem(CK('interviews'));
      if (cached) { const p=JSON.parse(cached); setUpcomingInterviews(p); return p; }
    } catch (e) { console.warn('fetchInterviews:', e?.message); }
    return [];
  }, [userProfile, profileScreenUser]);

  // Resolve the professional's identifiers — same logic as JobsOffersScreen.
  // The auth flow stores user records under one of several AsyncStorage keys
  // depending on entry point (login / biometric / refresh). Try them all
  // and accept the first that yields id/email.
  const resolveProfessionalIdentifiers = useCallback(async () => {
    const candidateKeys = ['user_data', 'user_profile', 'auth_user', 'userData', 'currentUser'];
    let id = null, email = null;
    for (const k of candidateKeys) {
      try {
        const raw = await AsyncStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const u = parsed?.user || parsed;
        if (!id    && (u?.id || u?.user_id))      id    = u.id || u.user_id;
        if (!email && u?.email)                   email = u.email;
        if (id && email) break;
      } catch {}
    }
    if (!email) email = await AsyncStorage.getItem('user_email').catch(() => null);
    if (!id)    id    = await AsyncStorage.getItem('user_id').catch(() => null);
    // Fall back to the live context if AsyncStorage hasn't been hydrated yet
    if (!id)    id    = userProfile?.id || userProfile?.user_id || profileScreenUser?.id || null;
    if (!email) email = userProfile?.email || profileScreenUser?.email || null;
    return { id, email };
  }, [userProfile, profileScreenUser]);

  const fetchOffers = useCallback(async () => {
    try {
      const { id, email } = await resolveProfessionalIdentifiers();
      // Without a user identifier we can't filter — bail rather than dumping
      // every offer in the company onto the dashboard.
      if (!id && !email) {
        console.log('[Dashboard] fetchOffers: no user identifier resolved');
        const cached = await AsyncStorage.getItem(CK('offers'));
        if (cached) { try { setActiveOffers(JSON.parse(cached)); } catch {} }
        return [];
      }

      // Parallel queries: by user_id (canonical) AND by candidate_email
      // (fallback for legacy offers created before the candidate had a
      // users row). De-dupe by id.
      const queries = [];
      if (id)    queries.push(`/api/data/job_offers?user_id=${id}&limit=50`);
      if (email) queries.push(`/api/data/job_offers?candidate_email=${encodeURIComponent(email)}&limit=50`);
      console.log('[Dashboard] fetchOffers firing:', queries);

      const results = await Promise.all(queries.map(q => apiFetch(q).catch(() => ({ data: [] }))));
      const seen = new Set();
      const merged = [];
      for (const r of results) {
        const arr = Array.isArray(r) ? r : (r?.data || r?.results || []);
        for (const o of arr) {
          if (!o?.id || seen.has(o.id)) continue;
          seen.add(o.id);
          merged.push(o);
        }
      }
      console.log('[Dashboard] fetchOffers merged:', merged.length);

      // Sort newest first — the offer section shows up to 3 cards
      merged.sort((a, b) => new Date(b.offer_date || b.created_at || 0) - new Date(a.offer_date || a.created_at || 0));

      setActiveOffers(merged);
      await AsyncStorage.setItem(CK('offers'), JSON.stringify(merged));
      return merged;
    } catch (e) {
      console.warn('fetchOffers:', e?.message);
      try {
        const cached = await AsyncStorage.getItem(CK('offers'));
        if (cached) { setActiveOffers(JSON.parse(cached)); }
      } catch {}
    }
    return [];
  }, [resolveProfessionalIdentifiers]);

  const fetchJobs = useCallback(async () => {
    try {
      const r = await apiFetch('/api/data/jobs?is_active=true&sort=created_at&order=desc&limit=12');
      if (!r.error && Array.isArray(r)) {
        setRecommendedJobs(r);
        await AsyncStorage.setItem(CK('jobs'), JSON.stringify(r));
        return r;
      }
      const cached = await AsyncStorage.getItem(CK('jobs'));
      if (cached) { const p=JSON.parse(cached); setRecommendedJobs(p); return p; }
    } catch {}
    return [];
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const r = await apiFetch('/api/data/notifications?sort=created_at&order=desc&limit=20');
      if (!r.error && Array.isArray(r)) { setNotifications(r); return r; }
    } catch {}
    return [];
  }, []);

  const fetchSavedJobs = useCallback(async () => {
    try {
      const r = await apiFetch('/api/data/saved_jobs?sort=created_at&order=desc&limit=20');
      if (!r.error && Array.isArray(r)) {
        setSavedJobs(r);
        await AsyncStorage.setItem(CK('saved_jobs'), JSON.stringify(r));
        return r;
      }
      const cached = await AsyncStorage.getItem(CK('saved_jobs'));
      if (cached) { const p=JSON.parse(cached); setSavedJobs(p); return p; }
    } catch {}
    return [];
  }, []);

  const fetchSkills = useCallback(async () => {
    try {
      const uid = await AsyncStorage.getItem('user_id') || await AsyncStorage.getItem('userId');
      const r = await apiFetch(`/api/data/user_skills?user_id=${uid||''}`);
      if (!r.error && Array.isArray(r.data||r)) {
        const sk = r.data||r;
        setSkillsData(sk);
        await AsyncStorage.setItem(CK('skills'), JSON.stringify(sk));
        return sk;
      }
      const cached = await AsyncStorage.getItem(CK('skills'));
      if (cached) { const p=JSON.parse(cached); setSkillsData(p); return p; }
    } catch {}
    return [];
  }, []);

  // ── AI Matches: top 5 personalised matches using multi-factor scoring ──────
  // Same algorithm as the AI Job Matching screen — skills 40 / role 20 /
  // experience 15 / location 15 / salary 10. Anchors to the user's current
  // title to filter out off-target roles.
  const fetchAIMatches = useCallback(async () => {
    try {
      const profile = userProfile || {};
      const profSkills = (Array.isArray(profile.skills) ? profile.skills : [])
        .map(s => typeof s === 'string' ? s : (s.name || s.skill_name || '')).filter(Boolean);
      const profTitle  = (profile.job_title || profile.current_title || profile.profession || '').toLowerCase();
      const profLoc    = (profile.location || '').toLowerCase();
      const profYrs    = Number(profile.experience_years || profile.yearsOfExperience || 0);

      // Anchor keyword for role match (strip seniority noise)
      const titleKw = profTitle.replace(/\b(senior|junior|lead|principal|staff|mid|associate)\b/gi,'').trim().split(/\s+/).slice(-2).join(' ');

      const r = await apiFetch(`/api/data/jobs?is_active=true&sort=created_at&order=desc&limit=50${titleKw?`&title=${encodeURIComponent(titleKw)}`:''}`);
      const jobs = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
      if (!jobs.length) return [];

      const scored = jobs.map(j => {
        // Skills match (40%)
        const jobSkills = toSkillStrings(Array.isArray(j.skills_required) ? j.skills_required : j.skills).map(s=>s.toLowerCase());
        const matched   = profSkills.filter(s => jobSkills.some(js => js.includes(s.toLowerCase()) || s.toLowerCase().includes(js)));
        const skillsScore = jobSkills.length ? (matched.length / jobSkills.length) * 40 : 20;
        // Role/title match (20%)
        const jt = (j.title || '').toLowerCase();
        const roleScore = !titleKw ? 10 : jt.includes(titleKw) ? 20 : jt.split(' ').some(w=>profTitle.includes(w)) ? 12 : 0;
        // Experience (15%)
        const expReq = Number(j.experience_min || 0);
        const expScore = profYrs >= expReq ? 15 : Math.max(0, 15 - (expReq - profYrs) * 3);
        // Location (15%)
        const jl = (j.location || '').toLowerCase();
        const locScore = !profLoc ? 8 : jl === profLoc ? 15 : jl.includes(profLoc.split(',')[0]) ? 12 : (j.job_type||'').toLowerCase().includes('remote') ? 10 : 4;
        // Salary (10%) — best-effort
        const salaryScore = (j.salary_min || j.salary_max) ? 10 : 5;

        const total = Math.round(skillsScore + roleScore + expScore + locScore + salaryScore);
        return { ...j, match_score: total, matched_skills: matched, skills_score: skillsScore, role_score: roleScore };
      });

      const top = scored
        .filter(j => j.role_score >= 10) // soft title-lock
        .sort((a,b) => b.match_score - a.match_score)
        .slice(0, 5);
      setAiMatches(top);
      await AsyncStorage.setItem(CK('ai_matches'), JSON.stringify(top));
      return top;
    } catch (e) {
      console.warn('fetchAIMatches:', e?.message);
      const cached = await AsyncStorage.getItem(CK('ai_matches'));
      if (cached) { const p = JSON.parse(cached); setAiMatches(p); return p; }
    }
    return [];
  }, [userProfile]);

  // ── Daily AI brief — personalised morning summary, cached for the day ──────
  const fetchDailyBrief = useCallback(async (apps=[], interviews=[]) => {
    try {
      const today = new Date().toDateString();
      const cached = await AsyncStorage.getItem(CK('daily_brief'));
      if (cached) {
        const { date, text } = JSON.parse(cached);
        if (date === today && text) { setDailyBrief(text); return text; }
      }
      const profile = userProfile || {};
      const upcomingCount = interviews.length;
      const recentApps = apps.slice(0, 3).map(a => `${a.job_title || a.position || 'job'} at ${a.employer_name || a.company || 'company'}`);
      const prompt = `You are a career coach. Write a 2-3 sentence morning brief for ${profile.full_name || 'this job seeker'} (${profile.job_title || 'professional'}). They have ${apps.length} active applications, ${upcomingCount} upcoming interviews. Recent applications: ${recentApps.join('; ')}. Be encouraging and specific. End with one actionable suggestion. Output the brief only, no preamble.`;

      setBriefLoading(true);
      const r = await apiFetch('/api/ai', { method:'POST', body: JSON.stringify({ prompt, max_tokens: 256 }) });
      const text = (r?.text || r?.content || r?.message || r?.advice || r?.choices?.[0]?.message?.content || '').trim();
      if (text) {
        setDailyBrief(text);
        await AsyncStorage.setItem(CK('daily_brief'), JSON.stringify({ date: today, text }));
      }
      return text;
    } catch (e) {
      console.warn('fetchDailyBrief:', e?.message);
    } finally {
      setBriefLoading(false);
    }
    return '';
  }, [userProfile]);

  // ── Career goals: weekly application/interview tracking + streak ───────────
  const computeCareerGoals = useCallback((apps=[], interviews=[]) => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const dateOfApp = a => new Date(a.applied_date || a.created_at || 0);
    const dateOfIv  = i => new Date(i.scheduled_at || i.scheduled_date || i.interview_date || 0);

    const appsThisWeek = apps.filter(a => dateOfApp(a) >= weekAgo).length;
    const interviewsThisWeek = interviews.filter(i => dateOfIv(i) >= weekAgo).length;

    // Streak: consecutive days back from today with at least 1 application
    const appDates = new Set(apps.map(a => dateOfApp(a).toDateString()));
    let streak = 0;
    for (let i = 0; i < 60; i++) {
      const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      if (appDates.has(d.toDateString())) streak++;
      else if (i > 0) break;  // allow today to be empty
    }

    const goals = { appsThisWeek, appsTarget: 5, interviewsThisWeek, interviewsTarget: 1, streakDays: streak };
    setCareerGoals(goals);
    AsyncStorage.setItem(CK('goals'), JSON.stringify(goals)).catch(()=>{});
    return goals;
  }, []);

  // ── Profile completion checklist — actionable items per missing piece ──────
  const computeProfileChecklist = useCallback(() => {
    const p = userProfile || {};
    const items = [];
    if (!p.profile_image && !p.avatar_url) items.push({ id:'photo', label:'Add a profile photo', icon:'camera-outline', boost:10, screen:'Profile' });
    if (!p.full_name)                       items.push({ id:'name',  label:'Set your full name', icon:'account-outline', boost:5,  screen:'Profile' });
    if (!p.job_title && !p.current_title)   items.push({ id:'title', label:'Add your job title', icon:'briefcase-outline', boost:10, screen:'Profile' });
    if (!p.summary && !p.bio && !p.about)   items.push({ id:'about', label:'Write your about/summary', icon:'text-box-outline', boost:15, screen:'Profile' });
    const skillCount = Array.isArray(p.skills) ? p.skills.length : 0;
    if (skillCount < 3) items.push({ id:'skills',     label:`Add ${3-skillCount} more skills`,           icon:'star-outline',        boost:15, screen:'Profile' });
    const expCount = Array.isArray(p.experience) ? p.experience.length : 0;
    if (expCount < 1)  items.push({ id:'experience', label:'Add your work experience',                 icon:'office-building',     boost:20, screen:'Profile' });
    const eduCount = Array.isArray(p.education) ? p.education.length : 0;
    if (eduCount < 1)  items.push({ id:'education',  label:'Add education',                            icon:'school-outline',      boost:10, screen:'Profile' });
    if (!p.linkedin_url && !p.linkedin)  items.push({ id:'linkedin', label:'Link your LinkedIn profile', icon:'linkedin',           boost:5,  screen:'Profile' });
    if (!p.resume_url)                   items.push({ id:'resume',   label:'Upload your CV/résumé',     icon:'file-document-outline', boost:10, screen:'BuildResume' });
    setProfileChecklist(items);
    return items;
  }, [userProfile]);

  const loadPortalData = useCallback(async () => {
    try {
      const [appsRaw, sdRaw, progRaw] = await Promise.all([
        AsyncStorage.getItem(SHARED.PORTAL_APPS),
        AsyncStorage.getItem(SHARED.SAVED),
        AsyncStorage.getItem(SHARED.PROGRAMS),
      ]);
      if (appsRaw) { const a=JSON.parse(appsRaw); if(Array.isArray(a)) setPortalApps(a); }
      if (sdRaw)   { const sd=JSON.parse(sdRaw); if(Array.isArray(sd.savedPrograms)) setPortalSaved(sd.savedPrograms); }
      if (progRaw) { const p=JSON.parse(progRaw); if(Array.isArray(p)) setPublishedPrograms(p.filter(x=>x.published)); }
    } catch {}
  }, []);

  // ── Load profile data synced from ProfileScreen ──────────────────────────────
  // ProfileScreen.saveAll() writes to PROF_KEYS.DASH and profileStats
  // This picks up: completion %, strength, photo (including base64), name, etc.
  const loadProfileScreenData = useCallback(async () => {
    try {
      // ── Set the canonical title FIRST. Read PROF_KEYS in priority order
      // and stop on the first non-empty value. If PROF_KEYS is empty
      // (fresh login, cleared cache, etc.), fall through to the DB so
      // the dashboard ALWAYS has a title to show without forcing the
      // user to open ProfileScreen first.
      let canonicalTitle = '';
      try {
        const profileRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE);
        const dashRaw    = await AsyncStorage.getItem(PROF_KEYS.DASH);
        const candidates = [
          profileRaw ? JSON.parse(profileRaw)?.personal?.jobTitle : null,
          dashRaw    ? JSON.parse(dashRaw)?.user?.jobTitle         : null,
          profileRaw ? JSON.parse(profileRaw)?.jobTitle            : null,
        ];
        const found = candidates.map(v => (v || '').trim()).find(v => v.length > 0);
        if (found) {
          console.log('[Dashboard:title-src] PROF_KEYS →', JSON.stringify(found));
          canonicalTitle = found;
          setProfileTitle(found);
        } else {
          // No local cache → ask the DB directly. Reading from /api/data/users
          // gives us whatever ProfileScreen.saveAll persisted earlier (the
          // canonical record). This is what makes the dashboard "just show"
          // the title on every fresh login without going through ProfileScreen.
          const uid = await AsyncStorage.getItem('user_id');
          if (uid && /^\d+$/.test(uid)) {
            try {
              const r = await apiFetch(`/api/data/users/${uid}`);
              const row = Array.isArray(r) ? r[0]
                        : (r?.data && !Array.isArray(r.data)) ? r.data
                        : (Array.isArray(r?.data) ? r.data[0] : r);
              const fromDb = (row?.job_title || row?.profession || row?.position || '').trim();
              if (fromDb) {
                console.log('[Dashboard:title-src] DB →', JSON.stringify(fromDb));
                canonicalTitle = fromDb;
                setProfileTitle(fromDb);
                // Seed AsyncStorage so the next focus reads instantly from
                // local cache. Wrap in the same shape ProfileScreen.saveAll
                // writes so loadProfileScreenData and any other reader can
                // pick it up without special-casing.
                try {
                  const existingProf = profileRaw ? JSON.parse(profileRaw) : {};
                  const merged = {
                    ...existingProf,
                    personal: { ...(existingProf.personal || {}), jobTitle: fromDb },
                  };
                  await AsyncStorage.setItem(PROF_KEYS.PROFILE, JSON.stringify(merged));
                  const existingDash = dashRaw ? JSON.parse(dashRaw) : {};
                  await AsyncStorage.setItem(PROF_KEYS.DASH, JSON.stringify({
                    ...existingDash,
                    user: { ...(existingDash.user || {}), jobTitle: fromDb },
                  }));
                } catch (e) { console.warn('[Dashboard] seed PROF_KEYS:', e?.message); }
              }
            } catch (e) {
              console.warn('[Dashboard] DB title lookup failed:', e?.message);
            }
          }
        }
      } catch (e) { console.warn('profileTitle read:', e?.message); }

      // ── ONE-SHOT BACKFILL to the users table. Without this, the
      // /api/data/users/{id} row keeps the stale value forever (whatever
      // it was at signup) and ProfileManagementService keeps returning
      // it. Push the latest local title up so the DB catches up.
      // Guarded by a flag so we don't re-push on every focus.
      if (canonicalTitle) {
        try {
          const uid = await AsyncStorage.getItem('user_id');
          if (uid && /^\d+$/.test(uid)) {
            const flagKey = `_titlePushed_users_${uid}_${canonicalTitle}`;
            const already = await AsyncStorage.getItem(flagKey);
            if (!already) {
              console.log('[Dashboard] backfilling users row', uid, 'title=', canonicalTitle);
              const r = await apiFetch(`/api/data/users/${uid}`, {
                method: 'PUT',
                body: JSON.stringify({
                  job_title:  canonicalTitle,
                  profession: canonicalTitle,
                  position:   canonicalTitle,
                }),
              });
              if (r && !r.error) {
                await AsyncStorage.setItem(flagKey, '1');
                console.log('[Dashboard] users title backfill OK');
              } else {
                console.warn('[Dashboard] users title backfill failed:', r?.message);
              }
            }
          }
        } catch (e) { console.warn('users backfill:', e?.message); }
      }

      // DIAGNOSTIC — dump every key ProfileScreen could plausibly write to
      // so we can see what shape the user's edit landed in. Print only in
      // dev so production logs stay clean.
      if (__DEV__) {
        const dbgKeys = [
          'prof_user_profile', 'prof_dashboard_data',
          'userProfile',       'dashboardData',
          'user_data',         'user_profile',
          'auth_user',         'profileStats',
          'currentUser',
        ];
        for (const k of dbgKeys) {
          try {
            const raw = await AsyncStorage.getItem(k);
            if (raw) {
              const parsed = JSON.parse(raw);
              // Hunt for any field that could be the job title
              const candidates = {
                'parsed.jobTitle':           parsed?.jobTitle,
                'parsed.job_title':          parsed?.job_title,
                'parsed.profession':         parsed?.profession,
                'parsed.personal.jobTitle':  parsed?.personal?.jobTitle,
                'parsed.personal.job_title': parsed?.personal?.job_title,
                'parsed.user.jobTitle':      parsed?.user?.jobTitle,
                'parsed.user.job_title':     parsed?.user?.job_title,
                'parsed.personalInfo.jobTitle': parsed?.personalInfo?.jobTitle,
              };
              const present = Object.entries(candidates).filter(([, v]) => v != null && v !== '');
              if (present.length) {
                console.log(`[Dashboard:diag] key=${k} →`, Object.fromEntries(present));
              }
            }
          } catch {}
        }
      }

      // 1. Read dashboard sync payload (written by ProfileScreen.saveAll)
      const dashRaw = await AsyncStorage.getItem(PROF_KEYS.DASH) ||
                      await AsyncStorage.getItem('dashboardData');
      if (dashRaw) {
        const dash = JSON.parse(dashRaw);
        if (dash?.user) {
          setProfileScreenUser(dash.user);
          // Sync title into userProfile so the customers-API stale value
          // can't win the welcome card render. (See note in section 3 below.)
          if (dash.user.jobTitle && String(dash.user.jobTitle).trim()) {
            const fresh = String(dash.user.jobTitle).trim();
            setUserProfile(prev => ({
              ...(prev || {}),
              job_title:     fresh,
              profession:    fresh,
              current_title: fresh,
            }));
          }
        }
        if (dash?.stats) {
          const comp = dash.stats.completionPercentage || dash.stats.completion_percentage || 0;
          const str  = dash.stats.profileStrength      || dash.stats.profile_strength      || 0;
          setProfileCompletion(comp);
          setProfileStrength(str);
        }
      }

      // 2. Read profileStats (also written by ProfileScreen)
      const statsRaw = await AsyncStorage.getItem('profileStats');
      if (statsRaw) {
        const ps = JSON.parse(statsRaw);
        if (ps.completionPercentage) setProfileCompletion(ps.completionPercentage);
        if (ps.profileStrength)      setProfileStrength(ps.profileStrength);
      }

      // 3. Read full profile for photo fallback (profileImageB64)
      const profileRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE) ||
                         await AsyncStorage.getItem('userProfile');
      if (profileRaw) {
        const prof = JSON.parse(profileRaw);
        if (prof?.personal) {
          const p = prof.personal;
          // Merge photo fields into profileScreenUser so UserPhoto can find them
          setProfileScreenUser(prev => ({
            ...(prev || {}),
            fullName:        p.fullName     || prev?.fullName     || '',
            email:           p.email        || prev?.email        || '',
            jobTitle:        p.jobTitle     || prev?.jobTitle     || '',
            company:         p.company      || prev?.company      || '',
            location:        p.location     || prev?.location     || '',
            availability:    p.availability || prev?.availability || '',
            profileImage:    p.profileImage    || prev?.profileImage    || null,
            profileImageB64: p.profileImageB64 || prev?.profileImageB64 || null,
          }));
          // SYNC the title into userProfile too. The customers API row is
          // often stale (it doesn't get PUT when the user saves ProfileScreen
          // locally), so without this, the welcome card flickers between
          // "General manager" (fresh, from ProfileScreen) and "Senior Engineer"
          // (stale, from /api/data/customers). Force them to agree on the
          // user's latest local edit.
          if (p.jobTitle && p.jobTitle.trim()) {
            const fresh = p.jobTitle.trim();
            setUserProfile(prev => ({
              ...(prev || {}),
              job_title:     fresh,
              profession:    fresh,
              current_title: fresh,
            }));
          }
        }

        // Fallback: if the stored completion % is missing/stale (e.g. written
        // before the profile data loaded) but the cached profile actually has
        // content, compute it locally so the ring never shows a false 0%.
        // Mirrors ProfileScreen.calcCompletion.
        const _pc = (() => {
          const p = prof.personal || {};
          const _s = v => (v == null ? '' : String(v));
          const f = ['fullName', 'email', 'phone', 'location', 'jobTitle', 'company', 'about'];
          let done = f.filter(k => _s(p[k]).trim()).length;
          const total = f.length + 1; // +1 for photo
          if (p.profileImage || p.profileImageB64) done++;
          let b = 0;
          if ((prof.experience     || []).length > 0) b += 8;
          if ((prof.education      || []).length > 0) b += 5;
          const sk = (prof.skills  || []).length;
          if (sk >= 5) b += 10; else if (sk > 0) b += 4;
          if ((prof.certifications || []).length > 0) b += 4;
          if ((prof.projects       || []).length > 0) b += 3;
          if ((prof.languages      || []).length > 0) b += 3;
          if (_s(p.linkedin).trim()) b += 5;
          return Math.max(0, Math.min(100, Math.round((done / total) * 100) + b));
        })();
        if (_pc > 0) setProfileCompletion(prev => Math.max(Number(prev) || 0, _pc));

        // Compute real years of experience from the work-experience entries
        // (career span: earliest start year -> latest end year, or now if a
        // role is current). Dates are free text, so we pull a 4-digit year.
        try {
          const exps = Array.isArray(prof.experience) ? prof.experience : [];
          const yearOf = s => { const m = String(s == null ? '' : s).match(/(19|20)\d{2}/); return m ? parseInt(m[0], 10) : null; };
          const now = new Date().getFullYear();
          let minStart = null, maxEnd = null;
          for (const e of exps) {
            const sy = yearOf(e.startDate || e.start_date);
            const current = !!(e.current || e.is_current) || /present/i.test(String(e.endDate || e.end_date || ''));
            const ey = current ? now : yearOf(e.endDate || e.end_date);
            if (sy && (minStart === null || sy < minStart)) minStart = sy;
            if (ey && (maxEnd === null || ey > maxEnd)) maxEnd = ey;
          }
          const yrs = minStart ? Math.max(0, (maxEnd || now) - minStart) : 0;
          setExpYears(yrs);
        } catch (_) {}
      }

      // 4. Also check standalone photo keys (written by ProfileScreen & BuildResume)
      const [photoUri, photoB64] = await Promise.all([
        AsyncStorage.getItem('profile_photo_uri'),
        AsyncStorage.getItem('profile_photo_b64'),
      ]);
      if (photoUri || photoB64) {
        setProfileScreenUser(prev => ({
          ...(prev || {}),
          profileImage:    photoUri || prev?.profileImage    || null,
          profileImageB64: photoB64 || prev?.profileImageB64 || null,
        }));
      }
    } catch(e) { console.warn('loadProfileScreenData:', e?.message); }
  }, []);

  const buildActivityFeed = useCallback((apps=[], interviews=[], offers=[]) => {
    const feed = [];
    apps.slice(0,3).forEach(app => {
      feed.push({ id:`app_${app.id}`, type:'application', title:`Applied to ${app.current_title||app.position||'Job'}`, date:app.created_at||app.applied_date, icon:'briefcase-outline', color:T.primary });
    });
    interviews.slice(0,2).forEach(iv => {
      feed.push({ id:`iv_${iv.id}`, type:'interview', title:`Interview ${iv.interview_type?`(${iv.interview_type})`:''} scheduled`, date:iv.interview_date, icon:'calendar-outline', color:T.primaryLight });
    });
    offers.slice(0,2).forEach(of => {
      feed.push({ id:`of_${of.id}`, type:'offer', title:`Offer for ${of.position||'Position'}`, date:of.created_at, icon:'gift-outline', color:T.success });
    });
    feed.sort((a,b)=>new Date(b.date||0)-new Date(a.date||0));
    const trimmed = feed.slice(0,8);
    setActivityFeed(trimmed);
    AsyncStorage.setItem(CK('activity'), JSON.stringify(trimmed)).catch(()=>{});
  }, []);

  const computeStats = useCallback((apps=[], interviews=[], offers=[]) => {
    const active    = apps.filter(a=>a.status==='active').length;
    const scheduled = interviews.filter(i=>i.status==='scheduled').length;
    const completed = interviews.filter(i=>i.status==='completed').length;
    const pending   = offers.filter(o=>o.status==='pending').length;
    // "Offers received" = offers sitting in the candidate's inbox waiting for
    // a response. Exclude accepted/declined/hired/expired — those are no
    // longer "received" in the conversational sense; the candidate has
    // moved past them. Recruiters fire SEND with status='sent', and offers
    // created without an explicit send live as 'pending' — both count as
    // "received" from the candidate's perspective.
    const RECEIVED_STATUSES = new Set(['sent', 'pending']);
    const offersReceived = offers.filter(o => {
      const s = String(o.status || '').toLowerCase();
      return RECEIVED_STATUSES.has(s);
    }).length;
    const successRate = apps.length>0 ? Math.round(interviews.length/apps.length*100) : 0;

    // Career score: blends profile strength (from ProfileScreen) + activity
    const activityScore = Math.min(60, Math.round(
      (apps.length>0?12:0) + (scheduled>0?12:0) + (completed>0?12:0) + (offers.length>0?12:0) + (pending===0&&offers.length>0?12:0)
    ));
    // Profile component: up to 40 points from profileStrength
    const profileScore = Math.round((profileStrength || profileCompletion || 0) * 0.4);
    const score = Math.min(100, activityScore + profileScore);
    setCareerScore(score);

    // Use real completion from ProfileScreen, fallback to API or default
    const realCompletion = profileCompletion || userProfile?.profile_completion || 0;

    const stats = {
      total_applications: apps.length,
      active_applications: active,
      interviews_scheduled: scheduled,
      interviews_completed: completed,
      offers_received: offersReceived,
      offers_pending: pending,
      success_rate: successRate,
      portal_applications: portalApps.length,
      portal_saved: portalSaved.length,
      notifications_count: notifications.filter(n=>!n.is_read).length,
      profile_completion: realCompletion,
      profile_views: userProfile?.profile_views || 0,
      jobs_saved: savedJobs.length,
    };
    setDashStats(stats);
    AsyncStorage.setItem(CK('stats'), JSON.stringify(stats)).catch(()=>{});
    return stats;
  }, [portalApps, portalSaved, notifications, userProfile, savedJobs, profileStrength, profileCompletion]);

  const loadCachedData = useCallback(async () => {
    try {
      const keys = ['stats','apps','interviews','offers','jobs','profile','saved_jobs','skills','activity'];
      const results = await Promise.all(keys.map(k => AsyncStorage.getItem(CK(k))));
      const [cs, ca, ci, co, cj, cn, csj, csk, caf] = results;
      if (cs) setDashStats(p=>({...p,...JSON.parse(cs)}));
      if (ca) setRecentApplications(JSON.parse(ca));
      if (ci) setUpcomingInterviews(JSON.parse(ci));
      if (co) setActiveOffers(JSON.parse(co));
      if (cj) setRecommendedJobs(JSON.parse(cj));
      if (cn) setUserProfile(JSON.parse(cn));
      if (csj) setSavedJobs(JSON.parse(csj));
      if (csk) setSkillsData(JSON.parse(csk));
      if (caf) setActivityFeed(JSON.parse(caf));
    } catch {}
  }, []);

  const loadAllData = useCallback(async (showLoader=true) => {
    if (showLoader) setIsLoading(true);
    setErrorMessage(null);
    try {
      // Load ProfileScreen data FIRST so completion/strength are available for computeStats
      await loadProfileScreenData();
      // fetchUserProfile returns the fresh profile object directly. We pass its
      // email to fetchApplications/fetchInterviews because React state from
      // setUserProfile() won't be visible inside those callbacks on this tick —
      // they captured the previous render's state. Without this, the first
      // load fires the candidate-scoped API calls with no email and gets
      // wrong/empty data, which is why a manual Profile-refresh used to "fix" it.
      const freshProfile = await fetchUserProfile();
      const email = freshProfile?.email
                 || (await AsyncStorage.getItem('user_email'))
                 || (await AsyncStorage.getItem('userEmail'))
                 || null;
      const [apps, interviews, offers] = await Promise.all([
        fetchApplications(email), fetchInterviews(email), fetchOffers(),
      ]);
      await Promise.all([fetchJobs(), fetchNotifications(), loadPortalData(), fetchSavedJobs(), fetchSkills()]);
      computeStats(apps, interviews, offers);
      buildActivityFeed(
        Array.isArray(apps) ? apps : [],
        Array.isArray(interviews) ? interviews : [],
        Array.isArray(offers) ? offers : [],
      );
      // New widgets — independent, errors don't block the dashboard
      const safeApps = Array.isArray(apps) ? apps : [];
      const safeIvs  = Array.isArray(interviews) ? interviews : [];
      computeCareerGoals(safeApps, safeIvs);
      computeProfileChecklist();
      // AI calls in parallel, fire-and-forget so the dash renders fast
      Promise.allSettled([
        fetchAIMatches(),
        fetchDailyBrief(safeApps, safeIvs),
      ]);
    } catch(e) {
      setErrorMessage('Could not refresh data. Pull down to retry.');
    }
    setIsLoading(false);
    setRefreshing(false);
  }, [loadProfileScreenData, fetchUserProfile, fetchApplications, fetchInterviews, fetchOffers, fetchJobs, fetchNotifications, loadPortalData, fetchSavedJobs, fetchSkills, computeStats, buildActivityFeed, computeCareerGoals, computeProfileChecklist, fetchAIMatches, fetchDailyBrief]);

  // ═════════════════════════════════════════════════════════════════════════════
  // LIFECYCLE
  // ═════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    loadCachedData().then(()=>loadAllData(true));
    Animated.timing(fadeAnim,{toValue:1,duration:800,useNativeDriver:true}).start();
    setAiMessages([{id:1,text:"Hi! I'm your AI Career Assistant. Ask me about job searching, interview prep, salary negotiation, or career planning!",sender:'ai',timestamp:new Date()}]);
  }, []);

  // Reload profile data every time screen comes into focus
  // (picks up changes from ProfileScreen, BuildResume, etc.)
  useFocusEffect(useCallback(()=>{
    loadProfileScreenData();
    loadPortalData();
    loadAllData(false);
  },[]));

  const onRefresh = useCallback(() => { setRefreshing(true); loadAllData(false); }, [loadAllData]);

  // ═════════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═════════════════════════════════════════════════════════════════════════════
  const nav = useCallback((screen, params={}) => {
    try { navigation.navigate(screen, params); }
    catch { Alert.alert('Coming Soon',`${screen} will be available soon!`); }
  }, [navigation]);

  const applyToJob = useCallback(async (jobId, jobTitle) => {
    try {
      const payload = {
        job_id:jobId,
        candidate_name: userProfile?.full_name || 'Professional',
        candidate_email: userProfile?.email || '',
        source: 'professional_dashboard',
      };
      const r = await apiFetch('/api/recruitment/apply',{method:'POST',body:JSON.stringify(payload)});
      if (r && !r.error) {
        Alert.alert('✅ Applied!',`Application for ${jobTitle} submitted successfully.`);
        fetchApplications();
      } else {
        Alert.alert('Error', r?.message || 'Please try again.');
      }
    } catch { Alert.alert('Error','Network error. Please check your connection.'); }
  }, [userProfile, fetchApplications]);

  const withdrawApplication = useCallback((id) => {
    Alert.alert('Withdraw Application','Are you sure you want to withdraw this application?',[
      {text:'Cancel',style:'cancel'},
      {text:'Withdraw',style:'destructive',onPress:async()=>{
        const r = await apiFetch(`/api/data/applications/${id}`,{method:'PUT',body:JSON.stringify({status:'withdrawn'})});
        fetchApplications();
        Alert.alert('Withdrawn','Application has been withdrawn.');
      }},
    ]);
  }, [fetchApplications]);

  const respondToOffer = useCallback(async (offerId, action) => {
    const actionLabel = action==='accepted' ? 'accept' : 'decline';
    Alert.alert(
      `${action==='accepted'?'Accept':'Decline'} Offer`,
      `Are you sure you want to ${actionLabel} this offer?`,
      [
        {text:'Cancel',style:'cancel'},
        {text:action==='accepted'?'Accept':'Decline', style:action==='accepted'?'default':'destructive', onPress:async()=>{
          const today  = new Date().toISOString().slice(0, 10);
          const nowIso = new Date().toISOString();
          // Use the actual job_offers schema column names — generic data
          // handler silently drops unknowns, so getting these wrong fails
          // silently. accepted_date / declined_date are date columns;
          // response_date is the timestamp.
          const payload = action === 'accepted'
            ? { status: 'accepted', accepted_date: today,  response_date: nowIso }
            : { status: 'rejected', declined_date: today, response_date: nowIso };
          try {
            const r = await apiFetch(`/api/data/job_offers/${offerId}`, {
              method: 'PUT', body: JSON.stringify(payload),
            });
            if (r && !r.error) {
              // Find the related application and bump its stage so the
              // recruiter pipeline reflects the response. Look up by the
              // offer row's job_application_id (if set) or candidate_email.
              try {
                const offerRow = (activeOffers || []).find(o => String(o.id) === String(offerId));
                let appId = offerRow?.job_application_id || null;
                if (!appId && offerRow?.candidate_email) {
                  const ar = await apiFetch(`/api/data/applications?candidate_email=${encodeURIComponent(offerRow.candidate_email)}&limit=10`);
                  const rows = Array.isArray(ar) ? ar : (ar?.data || []);
                  const match = rows.find(a => offerRow.job_id && a.job_id === offerRow.job_id) || rows[0];
                  appId = match?.id || null;
                }
                if (appId) {
                  await apiFetch(`/api/recruitment/applicant/${appId}/stage`, {
                    method: 'POST',
                    body: JSON.stringify({
                      stage:  action === 'accepted' ? 'offer_accepted' : 'rejected',
                      status: action === 'accepted' ? 'accepted'        : 'declined',
                      note:   action === 'accepted'
                        ? `Candidate accepted offer for ${offerRow?.position || ''}`
                        : `Candidate declined offer for ${offerRow?.position || ''}`,
                    }),
                  });
                }
              } catch (e) { console.warn('respondToOffer stage:', e?.message); }

              Alert.alert('Done', `Offer ${action==='accepted'?'accepted 🎉':'declined'}.`);
              fetchOffers();
            } else {
              throw new Error(r?.error || 'PUT failed');
            }
          } catch (e) {
            console.warn('respondToOffer:', e?.message);
            Alert.alert('Error','Could not respond to offer.');
          }
        }},
      ]
    );
  }, [fetchOffers, activeOffers]);

  const saveJob = useCallback(async (job) => {
    try {
      const payload = { job_id:job.id, title:job.title, company:job.department, location:job.location, salary_min:job.salary_min, salary_max:job.salary_max };
      await apiFetch('/api/data/saved_jobs',{method:'POST',body:JSON.stringify(payload)});
      Alert.alert('Saved','Job saved to your list.');
      fetchSavedJobs();
    } catch { Alert.alert('Error','Could not save job.'); }
  }, [fetchSavedJobs]);

  const unsaveJob = useCallback(async (jobId) => {
    try {
      await apiFetch(`/api/data/saved_jobs/${jobId}`,{method:'DELETE'});
      fetchSavedJobs();
    } catch {}
  }, [fetchSavedJobs]);

  const searchJobs = useCallback(async (q) => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await apiFetch(`/api/data/jobs?title=${encodeURIComponent(q)}&limit=15&sort=created_at&order=desc`);
      if (!r.error && Array.isArray(r)) { setSearchResults(r); }
      else {
        const r2 = await apiFetch('/api/data/jobs?limit=15&sort=created_at&order=desc');
        if (!r2.error && Array.isArray(r2)) setSearchResults(r2.filter(j=>(j.title||'').toLowerCase().includes(q.toLowerCase())));
      }
    } catch {}
    setSearching(false);
  }, []);

  const markNotifRead = useCallback(async (id) => {
    await apiFetch(`/api/data/notifications/${id}`,{method:'PUT',body:JSON.stringify({is_read:true})}).catch(()=>{});
    setNotifications(prev=>prev.map(n=>n.id===id?{...n,is_read:true}:n));
    setDashStats(prev=>({...prev,notifications_count:Math.max(0,prev.notifications_count-1)}));
  }, []);

  const markAllNotifsRead = useCallback(async () => {
    const unreadIds = notifications.filter(n=>!n.is_read).map(n=>n.id);
    Promise.all(unreadIds.map(id=>apiFetch(`/api/data/notifications/${id}`,{method:'PUT',body:JSON.stringify({is_read:true})}))).catch(()=>{});
    setNotifications(prev=>prev.map(n=>({...n,is_read:true})));
    setDashStats(prev=>({...prev,notifications_count:0}));
  }, [notifications]);

  const handleShareProfile = useCallback(async () => {
    try {
      // Single source of truth for the title — same one the welcome card uses.
      const title = profileTitle || 'Professional';
      await Share.share({
        title: `${name} - Professional Profile`,
        message: `Check out ${name}'s professional profile on JOBENG – ${title} with ${dashStats.total_applications} active applications.`,
      });
    } catch {}
  }, [name, userProfile, profileScreenUser, dashStats]);

  const handleLogout = useCallback(() => {
    Alert.alert('Logout','Are you sure you want to logout?',[
      {text:'Cancel',style:'cancel'},
      {text:'Logout',style:'destructive',onPress:async()=>{
        const keys = ['stats','apps','interviews','offers','jobs','profile','saved_jobs','skills','activity'].map(k=>CK(k));
        await AsyncStorage.multiRemove(keys).catch(()=>{});
        navigation.reset({index:0,routes:[{name:'Home'}]});
      }},
    ]);
  }, [navigation]);

  // ═════════════════════════════════════════════════════════════════════════════
  // AI CHAT
  // ═════════════════════════════════════════════════════════════════════════════
  const sendAIMessage = useCallback(async () => {
    if (!inputMessage.trim()) return;
    const userMsg = {id:Date.now(),text:inputMessage,sender:'user',timestamp:new Date()};
    setAiMessages(prev=>[...prev,userMsg]);
    setInputMessage('');
    setIsAITyping(true);
    try {
      // Same source the welcome card displays — keeps the AI's advice
      // accurate to the user's actual current role.
      const profileTitleForAI = profileTitle || 'Professional';
      const prompt = `You are a UK career advisor for JobEng.\nUser: ${userProfile?.full_name||'Professional'}, ${profileTitleForAI}, ${userProfile?.experience_years||5} years exp.\nStats: ${dashStats.total_applications} apps, ${dashStats.interviews_scheduled} interviews, ${dashStats.offers_received} offers.\nPortal: ${portalApps.length} internship apps.\nQuestion: "${userMsg.text}"\nProvide concise, actionable UK career advice in under 120 words.`;
      const r = await apiFetch('/api/ai',{method:'POST',body:JSON.stringify({prompt, max_tokens: 400})});
      const text = r?.text || r?.content || r?.message || r?.choices?.[0]?.message?.content || r?.advice || "I'm here to help! Ask about job searching, interview prep, salary negotiation, or career planning.";
      setAiMessages(prev=>[...prev,{id:Date.now()+1,text,sender:'ai',timestamp:new Date()}]);
    } catch {
      setAiMessages(prev=>[...prev,{id:Date.now()+1,text:"I'm here to help! Ask about job searching, interview prep, salary negotiation, or UK career planning.",sender:'ai',timestamp:new Date()}]);
    }
    setIsAITyping(false);
    setTimeout(()=>chatScrollRef.current?.scrollToEnd({animated:true}),200);
  }, [inputMessage, userProfile, dashStats, portalApps]);

  const AI_QUICK_PROMPTS = [
    'How to improve my profile?',
    'Best interview tips',
    'Salary negotiation advice',
    'Career path suggestions',
    'How to get more interviews?',
    'CV writing tips',
    'LinkedIn optimisation',
    'Remote job strategies',
  ];

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: HEADER
  // ═════════════════════════════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={[T.primary, T.primaryLight]} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity style={st.hdrBtn} onPress={()=>navigation.canGoBack()?navigation.goBack():nav('ProfessionalHomeScreen')}>
          <Ionicons name="arrow-back" size={22} color={T.white}/>
        </TouchableOpacity>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={st.hdrTitle}>Professional Dashboard</Text>
        </View>
        <View style={{flexDirection:'row',alignItems:'center',gap:8}}>
          <TouchableOpacity style={st.hdrBtn} onPress={()=>setShowSearchModal(true)}>
            <Ionicons name="search-outline" size={20} color={T.white}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.hdrBtn} onPress={handleShareProfile}>
            <Feather name="share-2" size={18} color={T.white}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.hdrBtn} onPress={()=>setShowNotifModal(true)}>
            <Ionicons name="notifications-outline" size={20} color={T.white}/>
            <CountBadge count={unread}/>
          </TouchableOpacity>
          <TouchableOpacity style={st.hdrBtn} onPress={()=>nav('Profile',{userData:userProfile})}>
            <View style={st.avatarCircle}>
              <Text style={{fontSize:14,fontWeight:'700',color:T.white}}>{initials}</Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>
    </LinearGradient>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: WELCOME CARD
  // ═════════════════════════════════════════════════════════════════════════════
  const renderWelcomeCard = () => {
    // Diagnostic — log every job-title source so we can see which is stale
    // when dashboard vs ProfileScreen disagree. profileTitle is the new
    // single source of truth — read directly from PROF_KEYS.
    if (__DEV__) {
      console.log('[Dashboard:title]',
        'profileTitle=',                JSON.stringify(profileTitle),
        '| profileScreenUser.jobTitle=', JSON.stringify(profileScreenUser?.jobTitle),
        '| userProfile.job_title=',      JSON.stringify(userProfile?.job_title),
        '| userProfile.profession=',     JSON.stringify(userProfile?.profession),
      );
    }
    return (
    <Animated.View style={[st.welcomeCard,{opacity:fadeAnim}]}>
      {/* ── Greeting + Photo ── */}
      <View style={{alignItems:'center',marginBottom:16}}>
        <TouchableOpacity onPress={()=>nav('Profile',{userData:userProfile})} activeOpacity={0.8}>
          <UserPhoto profile={mergedPhotoProfile} size={80} initials={initials}/>
        </TouchableOpacity>
        <Text style={st.greetingText}>{greeting()}, {name.split(' ')[0]}! 👋</Text>
        {/* Single source of truth: profileTitle is loaded directly from
            PROF_KEYS by loadProfileScreenData on every focus. If empty,
            show a tap-to-add link instead of falling back to other
            services (which hold stale values from earlier sessions). */}
        {profileTitle ? (
          <Text style={st.welcomeRole}>{profileTitle}</Text>
        ) : (
          <TouchableOpacity onPress={() => nav('Profile', { userData: userProfile })}>
            <Text style={[st.welcomeRole, { color: T.primary, textDecorationLine: 'underline' }]}>
              + Add your job title
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* ── Score + Badges Row ── */}
      <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <View style={{flex:1,flexDirection:'row',gap:8,flexWrap:'wrap'}}>
          <View style={st.infoBadge}>
            <MaterialCommunityIcons name="crown" size={13} color="#f59e0b"/>
            <Text style={st.infoBadgeT}>Premium</Text>
          </View>
          {(profileScreenUser?.location || userProfile?.location) && (
            <View style={st.infoBadge}>
              <MaterialCommunityIcons name="map-marker" size={13} color={T.textSec}/>
              <Text style={st.infoBadgeT}>{profileScreenUser?.location || userProfile?.location}</Text>
            </View>
          )}
          {expYears > 0 && (
            <View style={st.infoBadge}>
              <MaterialCommunityIcons name="briefcase-clock" size={13} color={T.textSec}/>
              <Text style={st.infoBadgeT}>{expYears}y exp</Text>
            </View>
          )}
          {profileStrength > 0 && (
            <View style={st.infoBadge}>
              <MaterialCommunityIcons name="shield-check" size={13} color={profileStrength>=70?T.success:profileStrength>=40?T.warning:T.danger}/>
              <Text style={st.infoBadgeT}>Strength {profileStrength}%</Text>
            </View>
          )}
        </View>
        <TouchableOpacity style={st.careerScoreCircle} onPress={()=>setShowAnalytics(true)}>
          <Text style={st.careerScoreNum}>{careerScore}</Text>
          <Text style={st.careerScoreLbl}>Score</Text>
        </TouchableOpacity>
      </View>

      {/* Profile completion bar */}
      {profComp < 100 && (
        <View style={{marginTop:14}}>
          <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:5}}>
            <Text style={{fontSize:12,color:T.textSec,fontWeight:'600'}}>Profile Completion</Text>
            <Text style={{fontSize:12,fontWeight:'700',color:profComp>=80?T.success:profComp>=50?T.warning:T.danger}}>{profComp}%</Text>
          </View>
          <AnimatedBar progress={profComp} color={profComp>=80?T.success:profComp>=50?T.warning:T.danger} height={8}/>
          <Text style={{fontSize:11,color:T.textMut,marginTop:4}}>
            {profComp < 30 ? 'Add your experience, skills and photo to get noticed'
            : profComp < 60 ? 'Good start! Add certifications and projects to stand out'
            : profComp < 80 ? 'Almost there! Complete your about section and LinkedIn'
            : 'Just a few more details to reach 100%'}
          </Text>
          <TouchableOpacity style={st.completeBtn} onPress={()=>nav('Profile')}>
            <Text style={st.completeBtnT}>Complete Profile +{100-profComp}% boost →</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Error banner */}
      {errorMessage && (
        <TouchableOpacity style={{marginTop:10,backgroundColor:T.dangerBg,borderRadius:8,padding:10,flexDirection:'row',alignItems:'center',gap:8}} onPress={()=>{ setErrorMessage(null); onRefresh(); }}>
          <Ionicons name="alert-circle" size={16} color={T.danger}/>
          <Text style={{fontSize:12,color:T.danger,flex:1}}>{errorMessage}</Text>
          <Ionicons name="refresh" size={14} color={T.danger}/>
        </TouchableOpacity>
      )}
    </Animated.View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: QUICK STATS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderQuickStats = () => {
    const STAT_GAP = 10;
    const STAT_COLS = 4;
    const STAT_PADDING = 16;
    const STAT_CARD_W = (SCREEN_W - STAT_PADDING*2 - STAT_GAP*(STAT_COLS-1)) / STAT_COLS;

    const stats = [
      {label:'Applications',value:dashStats.total_applications,color:T.primary,icon:'briefcase-outline',screen:'JobApplications'},
      {label:'Interviews',  value:dashStats.interviews_scheduled,color:T.primaryLight,icon:'calendar-clock',screen:'InterviewDetails'},
      {label:'Offers Received', value:dashStats.offers_received,    color:T.success,icon:'handshake-outline',screen:'JobsOffersScreen'},
      {label:'Success Rate',value:`${dashStats.success_rate||0}%`,color:T.warning,icon:'trending-up',screen:null},
      {label:'Profile Views',value:dashStats.profile_views,    color:T.purple,icon:'eye-outline',screen:'Profile'},
      {label:'Jobs Saved',  value:dashStats.jobs_saved,         color:T.pink,icon:'bookmark-outline',screen:'FindJobs'},
      {label:'Internships', value:portalApps.length,            color:'#667eea',icon:'school-outline',screen:'StudentInternshipPortalScreen'},
      {label:'Notifications',value:unread,                     color:T.danger,icon:'bell-outline',screen:null,onPress:()=>setShowNotifModal(true)},
    ];
    return (
      <View style={[st.statsGrid,{paddingHorizontal:STAT_PADDING,gap:STAT_GAP}]}>
        {stats.map((s,i)=>(
          <TouchableOpacity key={i} style={[st.statCard,{width:STAT_CARD_W}]} onPress={()=>s.onPress ? s.onPress() : s.screen && nav(s.screen)} activeOpacity={0.75}>
            <View style={[st.statIconWrap,{backgroundColor:s.color}]}>
              <MaterialCommunityIcons name={s.icon} size={18} color={T.white}/>
            </View>
            <Text style={[st.statNum,{color:s.color}]}>{s.value}</Text>
            <Text style={st.statLbl} numberOfLines={2}>{s.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: INTERNSHIP SNAPSHOT
  // ═════════════════════════════════════════════════════════════════════════════
  const renderInternshipSnapshot = () => {
    if (portalApps.length===0 && publishedPrograms.length===0) return null;
    const cards = [
      {l:'Applied',v:portalApps.length,c:T.purple,i:'send-outline'},
      {l:'Saved',v:portalSaved.length,c:T.success,i:'bookmark-outline'},
      {l:'Live Programs',v:publishedPrograms.length,c:T.warning,i:'school-outline'},
      {l:'Interviews',v:portalApps.filter(a=>a.status==='Interview Scheduled').length,c:T.primaryLight,i:'calendar-outline'},
    ];
    return (
      <View style={st.section}>
        <SectionHdr icon="school" title="Internship Portal" iconColor={T.purple} actionLabel="Open Portal" onAction={()=>nav('StudentInternshipPortalScreen')}/>
        <View style={{paddingHorizontal:16,flexDirection:'row',flexWrap:'wrap',gap:10}}>
          {cards.map((c,i)=>(
            <TouchableOpacity key={i} style={[st.internCard,{borderTopColor:c.c,width:(SCREEN_W-42)/2}]} onPress={()=>nav('InternshipApplication')} activeOpacity={0.75}>
              <MaterialCommunityIcons name={c.i} size={20} color={c.c}/>
              <Text style={[st.internNum,{color:c.c}]}>{c.v}</Text>
              <Text style={st.internLbl}>{c.l}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {portalApps.slice(0,3).map((app,i)=>{
          const badge = scBadge(app.status);
          return (
            <TouchableOpacity key={i} style={[st.portalAppRow,{marginHorizontal:16,marginTop:i===0?10:6}]}
              onPress={()=>nav('InternshipApplication')} activeOpacity={0.75}>
              <View style={[st.portalDot,{backgroundColor:badge.text}]}/>
              <View style={{flex:1}}>
                <Text style={st.portalAppTitle} numberOfLines={1}>{app.programTitle||'Internship Application'}</Text>
                <Text style={st.portalAppCo}>{app.company||''}</Text>
              </View>
              <StatusBadge status={app.status}/>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: RECENT APPLICATIONS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderRecentApplications = () => {
    if (recentApplications.length===0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="file-document-outline" title="Recent Applications" actionLabel="See All" onAction={()=>nav('JobApplications')}/>
        <FlatList
          data={recentApplications.slice(0,8)}
          keyExtractor={item=>String(item.id)}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingHorizontal:16,gap:12}}
          renderItem={({item:app})=>{
            const badge = scBadge(app.status);
            // New endpoint provides JOINed fields; keep fallbacks for cached older shape.
            const jobTitle = app.job_title || app.current_title || app.position || 'Job Application';
            const company  = app.employer_name || app.company || app.candidate_location || '';
            const logo     = app.employer_logo;
            const matchScore = app.match_score || app.ai_score;
            return (
              <TouchableOpacity style={st.appCard} onPress={()=>nav('ApplicationsScreen',{applicationId:app.id})} activeOpacity={0.85}>
                <View style={{flexDirection:'row',alignItems:'center',marginBottom:8}}>
                  <View style={[st.stageDot,{backgroundColor:scStage(app.stage)}]}/>
                  <Text style={st.stageT} numberOfLines={1}>{(app.stage||'Applied').replace(/_/g,' ')}</Text>
                </View>
                {logo ? (
                  <View style={{flexDirection:'row',alignItems:'center',marginBottom:6}}>
                    <Image source={{uri:logo}} style={{width:18,height:18,borderRadius:4,marginRight:6}}/>
                    <Text style={st.appCo} numberOfLines={1}>{company}</Text>
                  </View>
                ) : (
                  <Text style={st.appCo} numberOfLines={1}>{company || 'UK'}</Text>
                )}
                <Text style={st.appTitle} numberOfLines={2}>{jobTitle}</Text>
                <StatusBadge status={app.status}/>
                <Text style={st.appDate}>{fmtDate(app.applied_date||app.created_at)}</Text>
                {matchScore ? (
                  <View style={st.aiScoreBadge}>
                    <MaterialCommunityIcons name="robot-outline" size={11} color={T.primary}/>
                    <Text style={st.aiScoreT}>AI: {Math.round(matchScore)}%</Text>
                  </View>
                ) : null}
                {app.status !== 'withdrawn' && app.status !== 'rejected' && (
                  <TouchableOpacity style={st.withdrawBtn} onPress={()=>withdrawApplication(app.id)}>
                    <Text style={st.withdrawBtnT}>Withdraw</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            );
          }}
        />
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: UPCOMING INTERVIEWS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderUpcomingInterviews = () => {
    if (upcomingInterviews.length===0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="calendar-clock" title="Upcoming Interviews" actionLabel="See All" onAction={()=>nav('InterviewDetails')}/>
        <View style={{paddingHorizontal:0}}>
          {upcomingInterviews.slice(0,4).map(interview => {
            const badge = scBadge(interview.status);
            // New endpoint aliases scheduled_date→scheduled_at, meeting_link→meeting_url
            const dateStr  = interview.scheduled_at || interview.scheduled_date || interview.interview_date || interview.scheduled_for;
            const d        = new Date(dateStr);
            const days     = daysBetween(d, new Date());
            const isToday    = days <= 0 && days > -1;
            const isTomorrow = days >= 0 && days < 2;
            const meetingUrl = interview.meeting_url || interview.meeting_link;
            const jobTitle   = interview.job_title || interview.position;
            const employer   = interview.employer_name || interview.company;
            // Resolve interviewer name: prefer JSONB array, fall back to interviewer_name singleton
            const panel      = Array.isArray(interview.interviewers) ? interview.interviewers : [];
            const interviewer = panel[0]?.name || interview.interviewer_name || interview.interviewer;
            return (
              <TouchableOpacity
                key={interview.id}
                style={[st.interviewCard, isToday && {borderLeftWidth:3,borderLeftColor:T.danger}]}
                onPress={()=>nav('VideoInterviewScreen',{interviewId:interview.id})}
                activeOpacity={0.85}
              >
                <View style={st.interviewDateBox}>
                  <Text style={st.interviewDay}>{d.getDate()}</Text>
                  <Text style={st.interviewMonth}>{d.toLocaleDateString('en-GB',{month:'short'})}</Text>
                </View>
                <View style={{flex:1}}>
                  <Text style={st.interviewTitle} numberOfLines={1}>
                    {jobTitle || (interview.interview_type
                      ? `${interview.interview_type.charAt(0).toUpperCase()+interview.interview_type.slice(1)} Interview`
                      : 'Interview')}
                  </Text>
                  <Text style={st.interviewTime}>
                    {employer ? `${employer} · ` : ''}{fmtTime(dateStr)}{interview.duration_minutes||interview.duration?` · ${interview.duration_minutes||interview.duration} min`:''}
                  </Text>
                  {interviewer && <Text style={st.interviewMeta} numberOfLines={1}>With: {interviewer}{panel.length>1?` +${panel.length-1}`:''}</Text>}
                  {meetingUrl && (
                    <TouchableOpacity
                      style={{flexDirection:'row',alignItems:'center',gap:4,marginTop:3}}
                      onPress={()=>Linking.openURL(meetingUrl).catch(()=>{})}>
                      <Ionicons name="videocam-outline" size={13} color={T.primary}/>
                      <Text style={{fontSize:12,color:T.primary,fontWeight:'600'}}>Join Video Call</Text>
                    </TouchableOpacity>
                  )}
                  {isToday && <Text style={{fontSize:11,color:T.danger,fontWeight:'700',marginTop:3}}>⚠️ Today!</Text>}
                  {!isToday && isTomorrow && <Text style={{fontSize:11,color:T.warning,fontWeight:'700',marginTop:3}}>Tomorrow</Text>}
                </View>
                <StatusBadge status={interview.status}/>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: RECOMMENDED JOBS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderRecommendedJobs = () => {
    if (recommendedJobs.length===0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="star-outline" title="Recommended Jobs" actionLabel="See All" onAction={()=>nav('FindJobs')}/>
        <FlatList
          data={recommendedJobs.slice(0,8)}
          keyExtractor={item=>String(item.id)}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingHorizontal:16,gap:12}}
          renderItem={({item:job})=>(
            <TouchableOpacity style={st.jobCard} onPress={()=>nav('JobDetails',{jobId:job.id,job})} activeOpacity={0.85}>
              <View style={{flexDirection:'row',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                <View style={[st.jobBadge,{backgroundColor:job.is_remote?T.successBg:'#dbeafe'}]}>
                  <Text style={[st.jobBadgeT,{color:job.is_remote?'#16a34a':'#2563eb'}]}>{job.is_remote?'Remote':job.job_type||'Full-time'}</Text>
                </View>
                {job.is_urgent&&<View style={[st.jobBadge,{backgroundColor:T.dangerBg}]}><Text style={[st.jobBadgeT,{color:'#dc2626'}]}>Urgent</Text></View>}
              </View>
              <Text style={st.jobTitle} numberOfLines={2}>{job.title||'Job Title'}</Text>
              <Text style={st.jobDept} numberOfLines={1}>{job.department||''}</Text>
              {job.location && (
                <View style={{flexDirection:'row',alignItems:'center',marginTop:4,gap:4}}>
                  <Ionicons name="location-outline" size={12} color={T.textSec}/>
                  <Text style={{fontSize:11,color:T.textSec}} numberOfLines={1}>{job.location}</Text>
                </View>
              )}
              <Text style={st.jobSalary}>{fmtSalary(job.salary_min,job.salary_max,job.salary_currency)}</Text>
              {job.required_skills?.length>0 && (
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:4,marginBottom:8}}>
                  {job.required_skills.slice(0,3).map((sk,i)=><View key={i} style={st.skillChip}><Text style={st.skillChipT}>{skillLabel(sk)}</Text></View>)}
                  {job.required_skills.length>3&&<Text style={{fontSize:10,color:T.textMut,alignSelf:'center'}}>+{job.required_skills.length-3}</Text>}
                </View>
              )}
              <View style={{flexDirection:'row',gap:8}}>
                <TouchableOpacity style={[st.quickApply,{flex:1}]} onPress={()=>applyToJob(job.id,job.title)}>
                  <Text style={st.quickApplyT}>Quick Apply</Text>
                </TouchableOpacity>
                <TouchableOpacity style={st.saveJobBtn} onPress={()=>saveJob(job)}>
                  <Ionicons name="bookmark-outline" size={16} color={T.primary}/>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          )}
        />
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: ACTIVE OFFERS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderActiveOffers = () => {
    // Show only offers awaiting the candidate's response. Accepted /
    // rejected / hired offers are historical and surface on
    // OfferHistoryScreen rather than the dashboard's live area.
    const RECEIVED_STATUSES = new Set(['sent', 'pending']);
    const receivedOffers = activeOffers.filter(o =>
      RECEIVED_STATUSES.has(String(o.status || '').toLowerCase())
    );
    if (receivedOffers.length === 0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="handshake" title="Job Offers Received" actionLabel="See All" onAction={()=>nav('JobsOffersScreen')}/>
        <View style={{paddingHorizontal:0}}>
          {receivedOffers.slice(0,3).map(offer => {
            const badge = scBadge(offer.status);
            const deadlineDays = offer.deadline ? daysBetween(offer.deadline, new Date()) : null;
            return (
              <TouchableOpacity key={offer.id} style={st.offerCard} onPress={()=>nav('OfferDetails',{offerId:offer.id, id:offer.id, offer})} activeOpacity={0.85}>
                <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                  <Text style={st.offerPos} numberOfLines={1}>{offer.position||'Position'}</Text>
                  <StatusBadge status={offer.status}/>
                </View>
                {[
                  ['cash',T.success,offer.offered_salary?`£${Number(offer.offered_salary).toLocaleString()}`:''],
                  ['map-marker-outline',T.textSec,offer.location],
                  ['calendar-outline',T.textSec,offer.start_date?`Start: ${fmtDate(offer.start_date)}`:''],
                  ['clock-alert-outline',deadlineDays!==null&&deadlineDays<=3?T.danger:T.warning,offer.deadline?`Deadline: ${fmtDate(offer.deadline)}${deadlineDays!==null?` (${deadlineDays}d left)`:''}`:''],
                ].filter(([,,v])=>v).map(([icon,color,val],i) => (
                  <View key={i} style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:4}}>
                    <MaterialCommunityIcons name={icon} size={15} color={color}/>
                    <Text style={{fontSize:13,color:'#475569'}}>{val}</Text>
                  </View>
                ))}
                {offer.status==='pending' && (
                  <View style={{flexDirection:'row',gap:10,marginTop:12}}>
                    <TouchableOpacity style={[st.offerBtn,{backgroundColor:T.success}]} onPress={()=>respondToOffer(offer.id,'accepted')}>
                      <Ionicons name="checkmark-circle" size={16} color={T.white}/>
                      <Text style={st.offerBtnT}>Accept</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[st.offerBtn,{backgroundColor:T.danger}]} onPress={()=>respondToOffer(offer.id,'declined')}>
                      <Ionicons name="close-circle" size={16} color={T.white}/>
                      <Text style={st.offerBtnT}>Decline</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: CAREER INSIGHTS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderCareerInsights = () => {
    const funnelData = [
      {l:'Applications',v:dashStats.total_applications,c:T.primary},
      {l:'Interviews',v:dashStats.interviews_scheduled,c:T.primaryLight},
      {l:'Completed',v:dashStats.interviews_completed,c:T.success},
      {l:'Offers',v:dashStats.offers_received,c:T.warning},
    ];
    const max = dashStats.total_applications || 1;
    return (
      <View style={st.section}>
        <SectionHdr icon="chart-line" title="Career Insights" actionLabel="Full Analytics" onAction={()=>setShowAnalytics(true)}/>
        <View style={{paddingHorizontal:16}}>
          <View style={st.funnelCard}>
            <Text style={st.funnelTitle}>Application Funnel</Text>
            {funnelData.map((row,i) => {
              const pct = Math.min(100, Math.round(row.v/max*100));
              return (
                <View key={i} style={{marginBottom:10}}>
                  <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
                    <Text style={{fontSize:12,color:T.textSec,fontWeight:'600'}}>{row.l}</Text>
                    <Text style={{fontSize:12,fontWeight:'700',color:row.c}}>{row.v}</Text>
                  </View>
                  <AnimatedBar progress={pct} color={row.c} height={8}/>
                </View>
              );
            })}
          </View>
          <View style={{flexDirection:'row',gap:12,marginTop:6}}>
            {[
              {l:'Success Rate',v:`${dashStats.success_rate||0}%`,c:T.success,i:'trophy-outline'},
              {l:'Career Score',v:`${careerScore}/100`,c:T.warning,i:'star-outline'},
              {l:'Internships',v:portalApps.length,c:T.purple,i:'school-outline'},
            ].map((m,i)=>(
              <View key={i} style={[st.metricCard,{borderTopColor:m.c}]}>
                <MaterialCommunityIcons name={m.i} size={22} color={m.c}/>
                <Text style={[st.metricVal,{color:m.c}]}>{m.v}</Text>
                <Text style={st.metricLbl}>{m.l}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: AI ASSISTANT CARD
  // ═════════════════════════════════════════════════════════════════════════════
  const renderAIAssistant = () => (
    <TouchableOpacity style={st.aiCard} onPress={()=>setShowAIChat(true)} activeOpacity={0.85}>
      <LinearGradient colors={[T.primary, T.primaryLight]} start={{x:0,y:0}} end={{x:1,y:1}} style={st.aiCardGrad}>
        <View style={st.aiIconWrap}>
          <MaterialCommunityIcons name="robot" size={30} color={T.white}/>
        </View>
        <View style={{flex:1}}>
          <Text style={st.aiTitle}>AI Career Assistant</Text>
          <Text style={st.aiSub}>Job matching · Interview prep · Salary insights · Career planning</Text>
        </View>
        <Ionicons name="chevron-forward" size={20} color="rgba(255,255,255,0.8)"/>
      </LinearGradient>
    </TouchableOpacity>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: ACTIVITY FEED
  // ═════════════════════════════════════════════════════════════════════════════
  const renderActivityFeed = () => {
    if (activityFeed.length===0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="timeline-clock-outline" title="Recent Activity" actionLabel="See All" onAction={()=>nav('AllActivityScreen')}/>
        <View style={{paddingHorizontal:16}}>
          {activityFeed.slice(0,5).map((act,i)=>(
            <View key={act.id||i} style={{flexDirection:'row',alignItems:'flex-start'}}>
              <View style={{alignItems:'center',marginRight:12}}>
                <View style={{width:32,height:32,borderRadius:16,backgroundColor:(act.color||T.primary)+'15',justifyContent:'center',alignItems:'center'}}>
                  <Ionicons name={act.icon||'time-outline'} size={16} color={act.color||T.primary}/>
                </View>
                {i<Math.min(activityFeed.length,5)-1 && <View style={{width:2,height:20,backgroundColor:T.border,marginVertical:2}}/>}
              </View>
              <View style={{flex:1,paddingBottom:8}}>
                <Text style={{fontSize:13,fontWeight:'600',color:T.text}}>{act.title}</Text>
                <Text style={{fontSize:11,color:T.textMut,marginTop:2}}>{fmtDate(act.date)}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: SKILLS OVERVIEW
  // ═════════════════════════════════════════════════════════════════════════════
  const renderSkillsOverview = () => {
    if (skillsData.length===0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="chart-bar" title="Skills Overview" actionLabel="Manage" onAction={()=>nav('SkillProgress')}/>
        <View style={{paddingHorizontal:16}}>
          <View style={{backgroundColor:T.card,borderRadius:14,padding:14,...SHADOW_SM,borderWidth:1,borderColor:T.border}}>
            {skillsData.slice(0,6).map((sk,i)=>{
              const level = parseInt(sk.proficiency_level||sk.level||50);
              const color = level>=80?T.success:level>=60?T.primaryLight:level>=40?T.warning:T.textMut;
              return (
                <View key={sk.id||i} style={{marginBottom:i<Math.min(skillsData.length,6)-1?10:0}}>
                  <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
                    <Text style={{fontSize:13,fontWeight:'600',color:T.text}} numberOfLines={1}>{skillLabel(sk)||'Skill'}</Text>
                    <Text style={{fontSize:12,fontWeight:'700',color}}>{level}%</Text>
                  </View>
                  <AnimatedBar progress={level} color={color} height={6}/>
                </View>
              );
            })}
            {skillsData.length>6 && (
              <TouchableOpacity style={{alignItems:'center',marginTop:10}} onPress={()=>nav('SkillProgress')}>
                <Text style={{fontSize:12,color:T.primary,fontWeight:'600'}}>+{skillsData.length-6} more skills →</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: SAVED JOBS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderSavedJobs = () => {
    if (savedJobs.length===0) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="bookmark-outline" title="Saved Jobs" actionLabel="See All" onAction={()=>nav('FindJobs')}/>
        <FlatList
          data={savedJobs.slice(0,6)}
          keyExtractor={item=>String(item.id)}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingHorizontal:16,gap:12}}
          renderItem={({item:sj})=>(
            <TouchableOpacity style={[st.jobCard,{width:210}]} onPress={()=>nav('JobDetails',{jobId:sj.job_id||sj.id,job:sj})} activeOpacity={0.85}>
              <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                <Text style={[st.jobTitle,{flex:1,marginRight:4}]} numberOfLines={2}>{sj.title||sj.job_title||'Saved Job'}</Text>
                <TouchableOpacity onPress={()=>unsaveJob(sj.id)} hitSlop={{top:8,bottom:8,left:8,right:8}}>
                  <Ionicons name="bookmark" size={18} color={T.warning}/>
                </TouchableOpacity>
              </View>
              <Text style={st.jobDept} numberOfLines={1}>{sj.company||sj.department||''}</Text>
              {(sj.location||sj.job_location) && (
                <View style={{flexDirection:'row',alignItems:'center',marginTop:2,gap:4}}>
                  <Ionicons name="location-outline" size={12} color={T.textSec}/>
                  <Text style={{fontSize:11,color:T.textSec}} numberOfLines={1}>{sj.location||sj.job_location}</Text>
                </View>
              )}
              <Text style={st.jobSalary}>{fmtSalary(sj.salary_min,sj.salary_max,sj.salary_currency)}</Text>
              <TouchableOpacity style={st.quickApply} onPress={()=>applyToJob(sj.job_id||sj.id,sj.title||sj.job_title)}>
                <Text style={st.quickApplyT}>Apply Now</Text>
              </TouchableOpacity>
            </TouchableOpacity>
          )}
        />
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: TILE SECTION
  // ═════════════════════════════════════════════════════════════════════════════
  // NEW: NEXT INTERVIEW COUNTDOWN — prominent card if interview within 24h
  // ═════════════════════════════════════════════════════════════════════════════
  const renderNextInterviewCountdown = () => {
    if (!upcomingInterviews.length) return null;
    const next = upcomingInterviews[0];
    const dateStr = next.scheduled_at || next.scheduled_date || next.interview_date;
    if (!dateStr) return null;
    const start = new Date(dateStr).getTime();
    const ms = start - Date.now();
    if (ms < 0 || ms > 24 * 60 * 60 * 1000) return null; // only show within 24h
    const hours = Math.floor(ms / 3600000);
    const mins  = Math.floor((ms % 3600000) / 60000);
    const meetingUrl = next.meeting_url || next.meeting_link;
    return (
      <View style={st.section}>
        <LinearGradient colors={['#dc2626','#ea580c']} style={{marginHorizontal:16,borderRadius:14,padding:16}}>
          <View style={{flexDirection:'row',alignItems:'center',marginBottom:8}}>
            <Ionicons name="alarm" size={20} color="#fff"/>
            <Text style={{color:'#fff',fontSize:13,fontWeight:'700',marginLeft:8,letterSpacing:1}}>INTERVIEW SOON</Text>
          </View>
          <Text style={{color:'#fff',fontSize:18,fontWeight:'700'}} numberOfLines={1}>
            {next.job_title || 'Interview'}{next.employer_name ? ` · ${next.employer_name}` : ''}
          </Text>
          <Text style={{color:'rgba(255,255,255,0.9)',fontSize:13,marginTop:4}}>
            Starts in {hours > 0 ? `${hours}h ` : ''}{mins}m · {fmtTime(dateStr)}
          </Text>
          <View style={{flexDirection:'row',marginTop:12,gap:8}}>
            <TouchableOpacity
              style={{flex:1,backgroundColor:'#fff',paddingVertical:10,borderRadius:8,alignItems:'center'}}
              onPress={()=>nav('VideoInterviewScreen',{interviewId:next.id})}>
              <Text style={{color:'#dc2626',fontWeight:'700'}}>Open Interview</Text>
            </TouchableOpacity>
            {meetingUrl && (
              <TouchableOpacity
                style={{flex:1,backgroundColor:'rgba(255,255,255,0.18)',paddingVertical:10,borderRadius:8,alignItems:'center',borderWidth:1,borderColor:'rgba(255,255,255,0.4)'}}
                onPress={()=>Linking.openURL(meetingUrl).catch(()=>{})}>
                <Text style={{color:'#fff',fontWeight:'700'}}>Join Call</Text>
              </TouchableOpacity>
            )}
          </View>
        </LinearGradient>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // NEW: DAILY AI BRIEF — personalised morning summary
  // ═════════════════════════════════════════════════════════════════════════════
  const renderDailyBrief = () => {
    if (!dailyBrief && !briefLoading) return null;
    return (
      <View style={st.section}>
        <View style={{marginHorizontal:16,backgroundColor:'#f0f9ff',borderRadius:14,padding:14,borderLeftWidth:4,borderLeftColor:T.primary}}>
          <View style={{flexDirection:'row',alignItems:'center',marginBottom:6}}>
            <MaterialCommunityIcons name="weather-sunny" size={18} color={T.primary}/>
            <Text style={{fontSize:13,fontWeight:'700',color:T.primary,marginLeft:6,letterSpacing:0.5}}>TODAY'S BRIEF</Text>
            <Text style={{fontSize:11,color:T.textMut,marginLeft:'auto'}}>{new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'short'})}</Text>
          </View>
          {briefLoading ? (
            <ActivityIndicator size="small" color={T.primary} style={{marginVertical:8}}/>
          ) : (
            <Text style={{fontSize:14,lineHeight:20,color:T.text}}>{dailyBrief}</Text>
          )}
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // NEW: AI MATCHES — top 5 personalised jobs with multi-factor scoring
  // ═════════════════════════════════════════════════════════════════════════════
  const renderAIMatches = () => {
    if (!aiMatches.length) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="robot-outline" title="AI Matches for You" iconColor={T.primary} actionLabel="See All" onAction={()=>nav('AIJobMatching')}/>
        <FlatList
          data={aiMatches}
          keyExtractor={j=>String(j.id)}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{paddingHorizontal:16,gap:12}}
          renderItem={({item:job}) => {
            const score = job.match_score || 0;
            const scoreColor = score >= 80 ? T.success : score >= 60 ? T.warning : T.primaryLight;
            return (
              <TouchableOpacity
                style={{width:260,backgroundColor:T.white,borderRadius:12,padding:14,borderWidth:1,borderColor:T.border,elevation:2}}
                onPress={()=>nav('JobDetails',{jobId:job.id})}
                activeOpacity={0.85}>
                <View style={{flexDirection:'row',alignItems:'center',marginBottom:8}}>
                  <View style={{width:36,height:36,borderRadius:18,backgroundColor:scoreColor+'20',justifyContent:'center',alignItems:'center'}}>
                    <Text style={{color:scoreColor,fontWeight:'800',fontSize:13}}>{score}%</Text>
                  </View>
                  <View style={{flex:1,marginLeft:10}}>
                    <Text style={{fontSize:14,fontWeight:'700',color:T.text}} numberOfLines={1}>{job.title}</Text>
                    <Text style={{fontSize:12,color:T.textSec}} numberOfLines={1}>{job.company_name || job.employer_name || job.company || ''}</Text>
                  </View>
                </View>
                {job.location ? (
                  <View style={{flexDirection:'row',alignItems:'center',marginBottom:4}}>
                    <Ionicons name="location-outline" size={12} color={T.textMut}/>
                    <Text style={{fontSize:11,color:T.textMut,marginLeft:4}} numberOfLines={1}>{job.location}</Text>
                  </View>
                ) : null}
                {(job.salary_min || job.salary_max) ? (
                  <View style={{flexDirection:'row',alignItems:'center',marginBottom:4}}>
                    <Ionicons name="cash-outline" size={12} color={T.textMut}/>
                    <Text style={{fontSize:11,color:T.textMut,marginLeft:4}}>
                      {job.salary_min ? `£${Number(job.salary_min).toLocaleString()}` : ''}
                      {job.salary_min && job.salary_max ? ' – ' : ''}
                      {job.salary_max ? `£${Number(job.salary_max).toLocaleString()}` : ''}
                    </Text>
                  </View>
                ) : null}
                {Array.isArray(job.matched_skills) && job.matched_skills.length ? (
                  <View style={{flexDirection:'row',flexWrap:'wrap',gap:4,marginTop:6}}>
                    {job.matched_skills.slice(0,3).map((s,i) => (
                      <View key={i} style={{backgroundColor:T.primary+'15',paddingHorizontal:8,paddingVertical:3,borderRadius:8}}>
                        <Text style={{fontSize:10,color:T.primary,fontWeight:'600'}}>✓ {skillLabel(s)}</Text>
                      </View>
                    ))}
                    {job.matched_skills.length > 3 && (
                      <Text style={{fontSize:10,color:T.textMut,alignSelf:'center'}}>+{job.matched_skills.length-3}</Text>
                    )}
                  </View>
                ) : null}
              </TouchableOpacity>
            );
          }}
        />
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // NEW: PROFILE COMPLETION CHECKLIST — actionable items
  // ═════════════════════════════════════════════════════════════════════════════
  const renderProfileChecklist = () => {
    if (!profileChecklist.length || profComp >= 95) return null;
    return (
      <View style={st.section}>
        <SectionHdr icon="checkbox-marked-circle-outline" title={`Boost Your Profile (+${profileChecklist.reduce((s,i)=>s+i.boost,0)}%)`} iconColor={T.warning}/>
        <View style={{marginHorizontal:16,backgroundColor:T.white,borderRadius:12,borderWidth:1,borderColor:T.border,overflow:'hidden'}}>
          {profileChecklist.slice(0,5).map((item, i) => (
            <TouchableOpacity
              key={item.id}
              style={{flexDirection:'row',alignItems:'center',padding:12,borderTopWidth:i>0?1:0,borderTopColor:T.border}}
              onPress={()=>nav(item.screen||'Profile')}
              activeOpacity={0.7}>
              <View style={{width:32,height:32,borderRadius:16,backgroundColor:T.warning+'15',justifyContent:'center',alignItems:'center'}}>
                <MaterialCommunityIcons name={item.icon} size={18} color={T.warning}/>
              </View>
              <Text style={{flex:1,fontSize:14,color:T.text,marginLeft:12}}>{item.label}</Text>
              <View style={{backgroundColor:T.success+'15',paddingHorizontal:8,paddingVertical:3,borderRadius:8,marginRight:8}}>
                <Text style={{fontSize:11,color:T.success,fontWeight:'700'}}>+{item.boost}%</Text>
              </View>
              <Ionicons name="chevron-forward" size={16} color={T.textMut}/>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // NEW: CAREER GOALS TRACKER — weekly targets + streak
  // ═════════════════════════════════════════════════════════════════════════════
  const renderCareerGoals = () => {
    const { appsThisWeek, appsTarget, interviewsThisWeek, interviewsTarget, streakDays } = careerGoals;
    if (!appsThisWeek && !interviewsThisWeek && !streakDays) return null;
    const appPct = Math.min(100, Math.round((appsThisWeek / appsTarget) * 100));
    const ivPct  = Math.min(100, Math.round((interviewsThisWeek / interviewsTarget) * 100));
    return (
      <View style={st.section}>
        <SectionHdr icon="target" title="This Week's Goals" iconColor={T.success}/>
        <View style={{marginHorizontal:16,backgroundColor:T.white,borderRadius:12,padding:14,borderWidth:1,borderColor:T.border}}>
          {/* Applications goal */}
          <View style={{marginBottom:12}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
              <Text style={{fontSize:13,fontWeight:'600',color:T.text}}>Applications</Text>
              <Text style={{fontSize:13,fontWeight:'700',color:appPct>=100?T.success:T.text}}>{appsThisWeek} / {appsTarget}</Text>
            </View>
            <View style={{height:6,backgroundColor:T.border,borderRadius:3,overflow:'hidden'}}>
              <View style={{height:'100%',width:`${appPct}%`,backgroundColor:appPct>=100?T.success:T.primary,borderRadius:3}}/>
            </View>
          </View>
          {/* Interviews goal */}
          <View style={{marginBottom:12}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
              <Text style={{fontSize:13,fontWeight:'600',color:T.text}}>Interviews booked</Text>
              <Text style={{fontSize:13,fontWeight:'700',color:ivPct>=100?T.success:T.text}}>{interviewsThisWeek} / {interviewsTarget}</Text>
            </View>
            <View style={{height:6,backgroundColor:T.border,borderRadius:3,overflow:'hidden'}}>
              <View style={{height:'100%',width:`${ivPct}%`,backgroundColor:ivPct>=100?T.success:T.primaryLight,borderRadius:3}}/>
            </View>
          </View>
          {/* Streak */}
          {streakDays > 0 && (
            <View style={{flexDirection:'row',alignItems:'center',backgroundColor:T.warning+'15',paddingHorizontal:12,paddingVertical:8,borderRadius:8}}>
              <Text style={{fontSize:18}}>🔥</Text>
              <Text style={{fontSize:13,fontWeight:'700',color:T.warning,marginLeft:8}}>
                {streakDays}-day application streak — keep it going!
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════════
  const renderTileSection = (title, icon, tiles) => (
    <View style={st.section}>
      <SectionHdr icon={icon} title={title}/>
      <View style={st.tilesGrid}>
        {tiles.map((t,i)=><DashboardTile key={i} {...t} navigation={navigation}/>)}
      </View>
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // MODALS
  // ═════════════════════════════════════════════════════════════════════════════

  // ── AI Chat Modal ──
  const renderAIChatModal = () => (
    <Modal visible={showAIChat} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAIChat(false)}>
      <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
        <LinearGradient colors={[T.primary, T.primaryLight]} style={{paddingHorizontal:20,paddingVertical:16,flexDirection:'row',alignItems:'center'}}>
          <View style={{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.2)',justifyContent:'center',alignItems:'center',marginRight:12}}>
            <MaterialCommunityIcons name="robot" size={22} color={T.white}/>
          </View>
          <View style={{flex:1}}>
            <Text style={{fontSize:17,fontWeight:'700',color:T.white}}>AI Career Assistant</Text>
            <Text style={{fontSize:12,color:'rgba(255,255,255,0.8)',marginTop:1}}>Personalised for {name}</Text>
          </View>
          <TouchableOpacity
            style={{flexDirection:'row',alignItems:'center',gap:8}}
            onPress={()=>{
              setAiMessages([{id:1,text:"Hi! I'm your AI Career Assistant. Ask me anything!",sender:'ai',timestamp:new Date()}]);
            }}>
            <View style={{padding:6,borderRadius:16,backgroundColor:'rgba(255,255,255,0.2)'}}>
              <Ionicons name="refresh" size={16} color={T.white}/>
            </View>
          </TouchableOpacity>
          <TouchableOpacity style={{padding:8,borderRadius:20,backgroundColor:'rgba(255,255,255,0.2)',marginLeft:8}} onPress={()=>setShowAIChat(false)}>
            <Ionicons name="close" size={22} color={T.white}/>
          </TouchableOpacity>
        </LinearGradient>
        <ScrollView ref={chatScrollRef} style={{flex:1,padding:16}} onContentSizeChange={()=>chatScrollRef.current?.scrollToEnd({animated:true})}>
          {aiMessages.map(msg=>(
            <View key={msg.id} style={[{marginBottom:12,maxWidth:'80%'},msg.sender==='user'?{alignSelf:'flex-end'}:{alignSelf:'flex-start'}]}>
              <View style={[{borderRadius:16,padding:12},msg.sender==='user'?{backgroundColor:T.primary,borderBottomRightRadius:4}:{backgroundColor:T.card,borderBottomLeftRadius:4,...SHADOW_SM}]}>
                <Text style={[{fontSize:14,lineHeight:20},msg.sender==='user'?{color:T.white}:{color:T.text}]}>{msg.text}</Text>
              </View>
              <Text style={{fontSize:10,color:T.textMut,marginTop:3,textAlign:msg.sender==='user'?'right':'left'}}>
                {msg.timestamp instanceof Date ? msg.timestamp.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : ''}
              </Text>
            </View>
          ))}
          {isAITyping && (
            <View style={{alignSelf:'flex-start',backgroundColor:T.card,borderRadius:16,borderBottomLeftRadius:4,padding:12,...SHADOW_SM,marginBottom:12}}>
              <Text style={{fontSize:13,color:T.textSec,fontStyle:'italic'}}>Analysing your profile…</Text>
            </View>
          )}
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginTop:8}}>
            {AI_QUICK_PROMPTS.map((q,i)=>(
              <TouchableOpacity key={i} style={{backgroundColor:T.primaryBg,borderRadius:16,paddingHorizontal:12,paddingVertical:7,borderWidth:1,borderColor:'#BFDBFE'}}
                onPress={()=>setInputMessage(q)}>
                <Text style={{fontSize:12,color:T.primary,fontWeight:'500'}}>{q}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={{height:20}}/>
        </ScrollView>
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'}>
          <View style={{flexDirection:'row',alignItems:'flex-end',backgroundColor:T.card,borderTopWidth:1,borderTopColor:T.border,paddingHorizontal:14,paddingVertical:10,gap:10}}>
            <TextInput
              style={{flex:1,backgroundColor:T.borderLight,borderRadius:20,paddingHorizontal:16,paddingVertical:10,maxHeight:100,fontSize:14,color:T.text}}
              placeholder="Ask about jobs, interviews, salary…"
              placeholderTextColor={T.textMut}
              value={inputMessage}
              onChangeText={setInputMessage}
              multiline
            />
            <TouchableOpacity
              style={[{width:42,height:42,borderRadius:21,backgroundColor:T.primary,justifyContent:'center',alignItems:'center'},!inputMessage.trim()&&{opacity:0.4}]}
              onPress={sendAIMessage}
              disabled={!inputMessage.trim()||isAITyping}>
              <Ionicons name="send" size={18} color={T.white}/>
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ── Notifications Modal ──
  const renderNotifModal = () => (
    <Modal visible={showNotifModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowNotifModal(false)}>
      <SafeAreaView style={{flex:1,backgroundColor:T.bg}}>
        <View style={{flexDirection:'row',alignItems:'center',padding:16,borderBottomWidth:1,borderBottomColor:T.border,backgroundColor:T.card}}>
          <TouchableOpacity style={{width:36,height:36,borderRadius:10,backgroundColor:T.borderLight,justifyContent:'center',alignItems:'center'}} onPress={()=>setShowNotifModal(false)}>
            <Ionicons name="close" size={20} color={T.textSec}/>
          </TouchableOpacity>
          <Text style={{flex:1,textAlign:'center',fontSize:16,fontWeight:'700',color:T.text}}>Notifications</Text>
          {unread > 0 && (
            <TouchableOpacity style={{backgroundColor:T.primary,borderRadius:8,paddingHorizontal:12,paddingVertical:7}} onPress={markAllNotifsRead}>
              <Text style={{color:T.white,fontWeight:'600',fontSize:12}}>Read All</Text>
            </TouchableOpacity>
          )}
          {unread === 0 && <View style={{width:70}}/>}
        </View>
        <ScrollView style={{flex:1,padding:16}}>
          {notifications.length===0 && <EmptyBlock icon="bell-off-outline" title="No notifications" subtitle="You're all caught up!"/>}
          {notifications.map(n=>(
            <TouchableOpacity key={n.id}
              style={[{flexDirection:'row',backgroundColor:n.is_read?T.bg:T.primaryBg,borderRadius:12,padding:14,marginBottom:8,borderLeftWidth:3,borderLeftColor:n.is_read?T.border:T.primary}]}
              onPress={()=>markNotifRead(n.id)}>
              <View style={{width:36,height:36,borderRadius:18,backgroundColor:n.is_read?T.border:'#BFDBFE',justifyContent:'center',alignItems:'center',marginRight:10}}>
                <Ionicons name={n.type==='interview'?'calendar-outline':n.type==='offer'?'briefcase-outline':'bell-outline'} size={18} color={n.is_read?T.textMut:T.primary}/>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:14,fontWeight:n.is_read?'500':'700',color:T.text,marginBottom:3}}>{n.title||n.message||'Notification'}</Text>
                {n.body&&<Text style={{fontSize:13,color:T.textSec,lineHeight:18}}>{n.body}</Text>}
                <Text style={{fontSize:11,color:T.textMut,marginTop:4}}>{fmtDate(n.created_at)}</Text>
              </View>
              {!n.is_read&&<View style={{width:10,height:10,borderRadius:5,backgroundColor:T.primary,marginTop:4}}/>}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ── Search Modal ──
  const renderSearchModal = () => (
    <Modal visible={showSearchModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowSearchModal(false)}>
      <SafeAreaView style={{flex:1,backgroundColor:T.bg}}>
        <View style={{flexDirection:'row',alignItems:'center',padding:14,gap:10,backgroundColor:T.card,borderBottomWidth:1,borderBottomColor:T.border}}>
          <TouchableOpacity onPress={()=>{setShowSearchModal(false);setSearchQuery('');setSearchResults([]);}}>
            <Ionicons name="arrow-back" size={22} color={T.textSec}/>
          </TouchableOpacity>
          <View style={{flex:1,flexDirection:'row',backgroundColor:T.borderLight,borderRadius:12,paddingHorizontal:12,paddingVertical:10,alignItems:'center',gap:8}}>
            <Ionicons name="search-outline" size={18} color={T.textMut}/>
            <TextInput
              style={{flex:1,fontSize:14,color:T.text,padding:0}}
              placeholder="Search jobs, companies, roles…"
              placeholderTextColor={T.textMut}
              value={searchQuery}
              onChangeText={q=>{setSearchQuery(q);if(q.length>2) searchJobs(q);}}
              autoFocus
            />
            {searchQuery.length>0 && (
              <TouchableOpacity onPress={()=>{setSearchQuery('');setSearchResults([]);}}>
                <Ionicons name="close-circle" size={18} color={T.textMut}/>
              </TouchableOpacity>
            )}
          </View>
        </View>
        {searching && <View style={{alignItems:'center',padding:24}}><ActivityIndicator size={28} color={T.primary}/></View>}
        <FlatList
          data={searchResults}
          keyExtractor={i=>String(i.id)}
          contentContainerStyle={{padding:16}}
          renderItem={({item})=>(
            <TouchableOpacity
              style={{flexDirection:'row',backgroundColor:T.card,borderRadius:12,padding:14,marginBottom:8,borderWidth:1,borderColor:T.border,...SHADOW_SM}}
              onPress={()=>{setShowSearchModal(false);setSearchQuery('');nav('JobDetails',{jobId:item.id,job:item});}}>
              <View style={{flex:1}}>
                <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginBottom:3}}>{item.title}</Text>
                <Text style={{fontSize:13,color:T.textSec}}>{item.department||''} · {item.location||'UK'}</Text>
                <Text style={{fontSize:13,fontWeight:'700',color:T.success,marginTop:3}}>{fmtSalary(item.salary_min,item.salary_max)}</Text>
              </View>
              <View style={{justifyContent:'center',gap:6}}>
                <TouchableOpacity style={{backgroundColor:T.primary,borderRadius:10,paddingHorizontal:14,paddingVertical:8}} onPress={()=>applyToJob(item.id,item.title)}>
                  <Text style={{color:T.white,fontWeight:'700',fontSize:12}}>Apply</Text>
                </TouchableOpacity>
                <TouchableOpacity style={{alignItems:'center'}} onPress={()=>saveJob(item)}>
                  <Ionicons name="bookmark-outline" size={18} color={T.primary}/>
                </TouchableOpacity>
              </View>
            </TouchableOpacity>
          )}
          ListEmptyComponent={
            searchQuery.length>2&&!searching
              ? <EmptyBlock icon="search-web" title={`No results for "${searchQuery}"`} subtitle="Try different keywords"/>
              : searchQuery.length===0
              ? <EmptyBlock icon="magnify" title="Search for jobs" subtitle="Type at least 3 characters to search"/>
              : null
          }
        />
      </SafeAreaView>
    </Modal>
  );

  // ── Analytics Modal ──
  const renderAnalyticsModal = () => (
    <Modal visible={showAnalytics} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAnalytics(false)}>
      <SafeAreaView style={{flex:1,backgroundColor:T.bg}}>
        <LinearGradient colors={[T.primary, T.primaryLight]} style={{paddingHorizontal:16,paddingVertical:14,flexDirection:'row',alignItems:'center'}}>
          <TouchableOpacity style={{width:36,height:36,borderRadius:10,backgroundColor:'rgba(255,255,255,0.2)',justifyContent:'center',alignItems:'center',marginRight:12}} onPress={()=>setShowAnalytics(false)}>
            <Ionicons name="close" size={20} color={T.white}/>
          </TouchableOpacity>
          <Text style={{flex:1,fontSize:17,fontWeight:'700',color:T.white}}>Career Analytics</Text>
          <View style={{width:36,height:36,borderRadius:18,backgroundColor:'rgba(255,255,255,0.2)',justifyContent:'center',alignItems:'center'}}>
            <Text style={{color:T.white,fontSize:14,fontWeight:'800'}}>{careerScore}</Text>
          </View>
        </LinearGradient>
        <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
          {/* Career score */}
          <View style={{backgroundColor:T.card,borderRadius:14,padding:16,marginBottom:14,alignItems:'center',...SHADOW_SM,borderWidth:1,borderColor:T.border}}>
            <Text style={{fontSize:13,fontWeight:'600',color:T.textSec,marginBottom:10}}>Overall Career Score</Text>
            <View style={{width:120,height:120,borderRadius:60,borderWidth:8,borderColor:careerScore>=70?T.success:careerScore>=40?T.warning:T.danger,justifyContent:'center',alignItems:'center',marginBottom:10}}>
              <Text style={{fontSize:36,fontWeight:'900',color:T.text}}>{careerScore}</Text>
              <Text style={{fontSize:11,color:T.textSec}}>/ 100</Text>
            </View>
            <Text style={{fontSize:13,color:T.textSec,textAlign:'center',lineHeight:18}}>
              {careerScore>=70?'Excellent! You\'re on track for great opportunities.':careerScore>=40?'Good progress. Keep applying and building skills.':'Getting started. Complete your profile to boost your score.'}
            </Text>
          </View>

          {/* Profile Analytics — from ProfileScreen */}
          <View style={{backgroundColor:T.card,borderRadius:14,padding:16,marginBottom:14,...SHADOW_SM,borderWidth:1,borderColor:T.border}}>
            <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginBottom:14}}>Profile Analytics</Text>
            {/* Completion bar */}
            <View style={{marginBottom:14}}>
              <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
                <Text style={{fontSize:13,color:T.textSec,fontWeight:'600'}}>Profile Completion</Text>
                <Text style={{fontSize:14,fontWeight:'700',color:profComp>=80?T.success:profComp>=50?T.warning:T.danger}}>{profComp}%</Text>
              </View>
              <View style={{height:8,backgroundColor:T.borderLight,borderRadius:4,overflow:'hidden'}}>
                <View style={{height:8,borderRadius:4,backgroundColor:profComp>=80?T.success:profComp>=50?T.warning:T.danger,width:`${profComp}%`}}/>
              </View>
            </View>
            {/* Strength bar */}
            <View style={{marginBottom:14}}>
              <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}>
                <Text style={{fontSize:13,color:T.textSec,fontWeight:'600'}}>Profile Strength</Text>
                <Text style={{fontSize:14,fontWeight:'700',color:profileStrength>=70?T.success:profileStrength>=40?T.warning:T.danger}}>{profileStrength}%</Text>
              </View>
              <View style={{height:8,backgroundColor:T.borderLight,borderRadius:4,overflow:'hidden'}}>
                <View style={{height:8,borderRadius:4,backgroundColor:profileStrength>=70?T.success:profileStrength>=40?T.warning:T.danger,width:`${profileStrength}%`}}/>
              </View>
            </View>
            {/* Score breakdown */}
            {[
              {l:'Profile Component',v:`${Math.round((profileStrength||profComp)*0.4)}/40`,c:T.primary},
              {l:'Activity Component',v:`${Math.min(60,careerScore-Math.round((profileStrength||profComp)*0.4))}/60`,c:T.primaryLight},
            ].map((row,i)=>(
              <View key={i} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:i<1?1:0,borderBottomColor:T.borderLight}}>
                <Text style={{fontSize:13,color:T.textSec}}>{row.l}</Text>
                <Text style={{fontSize:14,fontWeight:'700',color:row.c}}>{row.v}</Text>
              </View>
            ))}
            <TouchableOpacity
              style={{backgroundColor:T.primaryBg,borderRadius:10,padding:10,marginTop:12,alignItems:'center',borderWidth:1,borderColor:'#BFDBFE'}}
              onPress={()=>{setShowAnalytics(false);nav('Profile');}}>
              <Text style={{fontSize:13,color:T.primary,fontWeight:'600'}}>Edit Profile to Boost Score →</Text>
            </TouchableOpacity>
          </View>

          {/* Application analytics */}
          <View style={{backgroundColor:T.card,borderRadius:14,padding:16,marginBottom:14,...SHADOW_SM,borderWidth:1,borderColor:T.border}}>
            <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginBottom:14}}>Application Analytics</Text>
            {[
              {l:'Total Applications',v:dashStats.total_applications,c:T.primary},
              {l:'Active Applications',v:dashStats.active_applications,c:T.primaryLight},
              {l:'Interviews Scheduled',v:dashStats.interviews_scheduled,c:'#3B82F6'},
              {l:'Interviews Completed',v:dashStats.interviews_completed,c:T.success},
              {l:'Offers Received',v:dashStats.offers_received,c:T.warning},
              {l:'Pending Offers',v:dashStats.offers_pending,c:'#D97706'},
            ].map((row,i)=>(
              <View key={i} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:i<5?1:0,borderBottomColor:T.borderLight}}>
                <Text style={{fontSize:13,color:T.textSec}}>{row.l}</Text>
                <Text style={{fontSize:14,fontWeight:'700',color:row.c}}>{row.v}</Text>
              </View>
            ))}
          </View>

          {/* Internship analytics */}
          <View style={{backgroundColor:T.card,borderRadius:14,padding:16,marginBottom:14,...SHADOW_SM,borderWidth:1,borderColor:T.border}}>
            <Text style={{fontSize:15,fontWeight:'700',color:T.text,marginBottom:14}}>Internship Portal</Text>
            {[
              {l:'Portal Applications',v:portalApps.length,c:T.purple},
              {l:'Saved Programmes',v:portalSaved.length,c:T.success},
              {l:'Live Programmes',v:publishedPrograms.length,c:T.warning},
              {l:'Interviews',v:portalApps.filter(a=>a.status==='Interview Scheduled').length,c:T.primaryLight},
              {l:'Accepted',v:portalApps.filter(a=>a.status==='Accepted').length,c:T.success},
            ].map((row,i)=>(
              <View key={i} style={{flexDirection:'row',justifyContent:'space-between',paddingVertical:8,borderBottomWidth:i<4?1:0,borderBottomColor:T.borderLight}}>
                <Text style={{fontSize:13,color:T.textSec}}>{row.l}</Text>
                <Text style={{fontSize:14,fontWeight:'700',color:row.c}}>{row.v}</Text>
              </View>
            ))}
          </View>

          <TouchableOpacity
            style={{backgroundColor:T.primary,borderRadius:12,paddingVertical:14,alignItems:'center',marginBottom:20}}
            onPress={()=>{setShowAnalytics(false);nav('StudentInternshipPortalScreen');}}>
            <Text style={{color:T.white,fontWeight:'700',fontSize:15}}>View Full Internship Portal →</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═════════════════════════════════════════════════════════════════════════════
  if (isLoading && recentApplications.length===0 && portalApps.length===0) {
    return (
      <SafeAreaView style={{flex:1,backgroundColor:T.primary}}>
        <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
        {renderHeader()}
        <View style={{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:T.bg}}>
          <ActivityIndicator size={40} color={T.primary}/>
          <Text style={{marginTop:16,fontSize:15,color:T.textSec,fontWeight:'500'}}>Loading your dashboard…</Text>
        </View>
      </SafeAreaView>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <SafeAreaView style={{flex:1,backgroundColor:T.primary}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      {renderHeader()}

      <ScrollView
        ref={scrollRef}
        style={{flex:1,backgroundColor:T.bg}}
        contentContainerStyle={{paddingBottom:100}}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[T.primary]} tintColor={T.primary}/>}
      >
        {renderWelcomeCard()}
        {renderNextInterviewCountdown()}
        {renderDailyBrief()}
        {renderQuickStats()}
        {renderCareerGoals()}
        {renderProfileChecklist()}
        {renderAIMatches()}
        {renderInternshipSnapshot()}
        {renderCareerInsights()}
        {renderAIAssistant()}
        {renderRecentApplications()}
        {renderUpcomingInterviews()}
        {renderRecommendedJobs()}
        {renderActiveOffers()}
        {renderActivityFeed()}
        {renderSkillsOverview()}
        {renderSavedJobs()}

        {/* ── Job Search Tiles ── */}
        {renderTileSection('Job Search Services','magnify',[
          {label:'Find Jobs',icon:'search',bg:T.primary,screen:'FindJobs'},
          {label:'Freelance Jobs',icon:'laptop',bg:'#fa709a',screen:'FreelanceJobs'},
          {label:'Entry Level',icon:'user-plus',bg:T.primaryLight,screen:'EntryLevelJobs'},
          {label:'Opportunities',icon:'gift',bg:T.warning,screen:'JobsOpportunities'},
        ])}

        {/* ── Applications Tiles ── */}
        {renderTileSection('Applications','file-document',[
          {label:'All Applications',icon:'file-alt',bg:T.primary,screen:'JobApplications'},
          {label:'Track Status',icon:'info-circle',bg:T.primaryLight,screen:'ApplicationsScreen'},
          {label:'Job Offers',icon:'handshake',bg:T.success,screen:'JobsOffersScreen'},
          {label:'Offer History',icon:'history',bg:'#4ecdc4',screen:'OfferHistoryScreen'},
        ])}

        {/* ── Interview Tiles ── */}
        {renderTileSection('Interview Management','calendar-clock',[
          {label:'Tests & Interviews',icon:'comments',bg:T.primary,screen:'TestsInterviews'},
          {label:'Interview Prep',icon:'chalkboard-teacher',bg:T.primaryLight,screen:'InterviewPrep'},
          {label:'Video Interview',icon:'video',bg:T.success,screen:'VideoInterview'},
          {label:'Interview Details',icon:'calendar-alt',bg:'#fa709a',screen:'InterviewDetails'},
          {label:'Reschedule',icon:'calendar-times',bg:T.danger,screen:'RescheduleInterview'},
          {label:'Interview History',icon:'calendar-check',bg:'#4ecdc4',screen:'InterviewHistoryScreen'},
        ])}

        {/* ── Career Dev Tiles ── */}
        {renderTileSection('Career Development','school',[
          {label:'Career Coaching',icon:'chalkboard-teacher',bg:T.primary,screen:'CareerCoachingScreen'},
          {label:'Internship Hub',icon:'graduation-cap',bg:T.purple,screen:'InternshipOpportunities'},
          {label:'Graduate Programs',icon:'university',bg:'#4ecdc4',screen:'GraduatePrograms'},
          {label:'Student Portal',icon:'school',bg:'#764ba2',screen:'StudentInternshipPortalScreen'},
          {label:'Skill Progress',icon:'chart-line',bg:T.success,screen:'SkillProgress'},
          {label:'Training',icon:'graduation-cap',bg:'#fa709a',screen:'DevelopmentTraining'},
        ])}

        {/* ── Career Events Tiles ── */}
        {renderTileSection('Career Events','calendar-star',[
          {label:'Career Fairs',icon:'users',bg:'#e67e22',screen:'CareerFairs'},
          {label:'Webinar Register',icon:'user-check',bg:T.primaryLight,screen:'WebinarRegistrationScreen'},
          {label:'Career Events',icon:'briefcase',bg:T.success,screen:'CareerEventScreen'},
        ])}

        {/* ── Professional Tools Tiles ── */}
        {renderTileSection('Professional Tools','tools',[
          {label:'Build Resume',icon:'file-alt',bg:T.primary,screen:'BuildResume'},
          {label:'My Documents',icon:'eye',bg:T.primaryLight,screen:'MyDocuments'},
          {label:'Upload Resume',icon:'upload',bg:T.success,screen:'UploadResume'},
          {label:'Portfolio',icon:'folder-open',bg:'#fa709a',screen:'Portfolio'},
          {label:'Profile Rating',icon:'star',bg:T.danger,screen:'ProfileRating'},
          {label:'All Activities',icon:'history',bg:'#5e3451',screen:'AllActivityScreen'},
        ])}

        {/* ── AI Services Tiles ── */}
        {renderTileSection('AI Services','robot',[
          {label:'AI Job Matching',icon:'robot',bg:T.primary,screen:'AIJobMatching',premium:true},
          {label:'AI Content Assist',icon:'brain',bg:T.primaryLight,screen:'AIContentAssistantScreen',premium:true},
        ])}

        {/* ── Comms Tiles ── */}
        {renderTileSection('Notifications & Messaging','cog',[
          {label:'Notifications',icon:'bell',bg:T.primary,screen:'Notifications'},
          {label:'Messages',icon:'envelope',bg:T.primaryLight,screen:'MessagesScreen'},
          {label:'New Message',icon:'comment-medical',bg:T.success,screen:'NewMessage'},
          {label:'Chat',icon:'comments',bg:'#fa709a',screen:'ChatDetail'},
        ])}

        {/* ── Logout ── */}
        <View style={{paddingHorizontal:16,marginTop:8}}>
          <TouchableOpacity style={st.logoutBtn} onPress={handleLogout}>
            <Ionicons name="log-out-outline" size={20} color={T.danger}/>
            <Text style={{fontSize:15,fontWeight:'700',color:T.danger}}>Logout</Text>
          </TouchableOpacity>
        </View>

        <View style={{height:20}}/>
      </ScrollView>

      {/* Bottom Nav */}
      <View style={st.bottomNav}>
        {[
          {icon:'view-dashboard',label:'Dashboard',screen:'ProfessionalDashboard',active:true},
          {icon:'magnify',label:'Search',screen:'SearchJobsScreen'},
          {icon:'school-outline',label:'Internships',screen:'StudentInternshipPortalScreen'},
          {icon:'account',label:'Profile',screen:'Profile'},
          {icon:'home',label:'Home',screen:'ProfessionalHomeScreen'},
        ].map((t,i)=>(
          <TouchableOpacity key={i} style={st.navItem} onPress={()=>nav(t.screen,t.params||{})} activeOpacity={0.7}>
            {t.active
              ? <View style={{padding:4,backgroundColor:T.primaryBg,borderRadius:10}}><MaterialCommunityIcons name={t.icon} size={22} color={T.primary}/></View>
              : <MaterialCommunityIcons name={t.icon} size={22} color={T.textSec}/>
            }
            <Text style={[{fontSize:10,marginTop:2,color:T.textSec},t.active&&{color:T.primary,fontWeight:'600'}]}>{t.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Modals */}
      {renderAIChatModal()}
      {renderNotifModal()}
      {renderSearchModal()}
      {renderAnalyticsModal()}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  // Header
  header:       {paddingHorizontal:16,paddingVertical:14},
  headerRow:    {flexDirection:'row',alignItems:'center'},
  hdrBtn:       {width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center',position:'relative'},
  hdrTitle:     {fontSize:18,fontWeight:'700',color:T.white},
  hdrSub:       {fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:2},
  notifBadge:   {position:'absolute',top:1,right:1,borderRadius:8,minWidth:14,height:14,justifyContent:'center',alignItems:'center',paddingHorizontal:2},
  notifBadgeT:  {color:T.white,fontSize:8,fontWeight:'700'},
  avatarCircle: {width:38,height:38,borderRadius:19,backgroundColor:'rgba(255,255,255,0.22)',justifyContent:'center',alignItems:'center',borderWidth:2,borderColor:T.white},

  // Welcome
  welcomeCard:  {backgroundColor:T.card,borderRadius:16,marginHorizontal:16,marginTop:16,marginBottom:8,padding:18,...SHADOW},
  greetingText: {fontSize:20,fontWeight:'800',color:T.text,marginTop:12,marginBottom:2,textAlign:'center'},
  welcomeName:  {fontSize:20,fontWeight:'700',color:T.text,marginBottom:3},
  welcomeRole:  {fontSize:14,color:T.textSec,textAlign:'center'},
  infoBadge:    {flexDirection:'row',alignItems:'center',backgroundColor:T.borderLight,paddingHorizontal:10,paddingVertical:5,borderRadius:12,gap:4},
  infoBadgeT:   {fontSize:12,fontWeight:'600',color:T.textSec},
  careerScoreCircle:{width:64,height:64,borderRadius:32,backgroundColor:T.primaryBg,justifyContent:'center',alignItems:'center',borderWidth:3,borderColor:T.primary},
  careerScoreNum:  {fontSize:22,fontWeight:'900',color:T.primary},
  careerScoreLbl:  {fontSize:9,color:T.textSec,fontWeight:'600'},
  completeBtn:  {backgroundColor:T.primaryBg,borderRadius:10,padding:10,marginTop:10,alignItems:'center',borderWidth:1,borderColor:'#BFDBFE'},
  completeBtnT: {fontSize:13,color:T.primary,fontWeight:'600'},

  // Stats grid
  statsGrid:    {flexDirection:'row',flexWrap:'wrap',marginBottom:6},
  statCard:     {alignItems:'center',backgroundColor:T.card,borderRadius:14,paddingVertical:14,paddingHorizontal:4,...SHADOW_SM},
  statIconWrap: {width:36,height:36,borderRadius:12,justifyContent:'center',alignItems:'center',marginBottom:6},
  statNum:      {fontSize:18,fontWeight:'900',marginBottom:2},
  statLbl:      {fontSize:10,color:T.textSec,textAlign:'center',fontWeight:'500',paddingHorizontal:2,lineHeight:13},

  // Internship
  internCard:   {backgroundColor:T.card,borderRadius:14,paddingVertical:14,paddingHorizontal:8,alignItems:'center',borderTopWidth:3,...SHADOW_SM,borderWidth:1,borderColor:T.border},
  internNum:    {fontSize:24,fontWeight:'900',marginTop:6,marginBottom:2},
  internLbl:    {fontSize:11,color:T.textSec,fontWeight:'600',textAlign:'center'},
  portalAppRow: {flexDirection:'row',alignItems:'center',backgroundColor:T.card,borderRadius:10,padding:12,borderWidth:1,borderColor:T.border,gap:10,...SHADOW_SM},
  portalDot:    {width:8,height:8,borderRadius:4},
  portalAppTitle:{fontSize:13,fontWeight:'600',color:T.text},
  portalAppCo:  {fontSize:11,color:T.textSec,marginTop:1},

  // Section
  section:      {marginBottom:20},
  sectionHdr:   {flexDirection:'row',alignItems:'center',paddingHorizontal:16,marginBottom:14},
  sectionTitle: {fontSize:17,fontWeight:'700',color:T.text,marginLeft:8,flex:1},
  seeAll:       {flexDirection:'row',alignItems:'center',gap:2},
  seeAllT:      {fontSize:13,color:T.primary,fontWeight:'600'},

  // Status badge
  statusBadge:  {paddingHorizontal:8,paddingVertical:4,borderRadius:8,alignSelf:'flex-start',marginTop:6},
  statusBadgeT: {fontSize:11,fontWeight:'600'},

  // Application cards
  appCard:      {width:200,backgroundColor:T.card,borderRadius:14,padding:14,...SHADOW_SM,borderWidth:1,borderColor:T.border},
  stageDot:     {width:8,height:8,borderRadius:4,marginRight:6},
  stageT:       {fontSize:11,color:T.textSec,fontWeight:'500',textTransform:'capitalize'},
  appTitle:     {fontSize:14,fontWeight:'600',color:T.text,marginBottom:3,lineHeight:18},
  appCo:        {fontSize:12,color:T.textSec},
  appDate:      {fontSize:11,color:T.textMut,marginTop:6},
  aiScoreBadge: {flexDirection:'row',alignItems:'center',backgroundColor:T.primaryBg,paddingHorizontal:8,paddingVertical:3,borderRadius:8,marginTop:6,alignSelf:'flex-start',gap:4},
  aiScoreT:     {fontSize:11,color:T.primary,fontWeight:'600'},
  withdrawBtn:  {marginTop:8,borderWidth:1,borderColor:'#FCA5A5',borderRadius:8,paddingVertical:6,alignItems:'center'},
  withdrawBtnT: {fontSize:11,color:T.danger,fontWeight:'600'},

  // Interview
  interviewCard:{flexDirection:'row',backgroundColor:T.card,borderRadius:14,padding:14,marginBottom:10,marginHorizontal:16,...SHADOW_SM,borderWidth:1,borderColor:T.border,alignItems:'center',gap:12},
  interviewDateBox:{width:52,height:52,borderRadius:12,backgroundColor:T.primaryBg,justifyContent:'center',alignItems:'center'},
  interviewDay: {fontSize:20,fontWeight:'700',color:T.primary},
  interviewMonth:{fontSize:10,fontWeight:'700',color:T.primaryLight,textTransform:'uppercase'},
  interviewTitle:{fontSize:14,fontWeight:'600',color:T.text},
  interviewTime:{fontSize:12,color:T.textSec,marginTop:2},
  interviewMeta:{fontSize:11,color:T.textMut,marginTop:2},

  // Job
  jobCard:      {width:240,backgroundColor:T.card,borderRadius:14,padding:16,...SHADOW_SM,borderWidth:1,borderColor:T.border},
  jobBadge:     {paddingHorizontal:8,paddingVertical:3,borderRadius:6},
  jobBadgeT:    {fontSize:10,fontWeight:'600'},
  jobTitle:     {fontSize:15,fontWeight:'700',color:T.text,marginBottom:3,lineHeight:20},
  jobDept:      {fontSize:12,color:T.textSec,marginBottom:4},
  jobSalary:    {fontSize:14,fontWeight:'700',color:T.success,marginVertical:6},
  skillChip:    {backgroundColor:T.borderLight,paddingHorizontal:8,paddingVertical:3,borderRadius:6},
  skillChipT:   {fontSize:10,color:'#475569',fontWeight:'500'},
  quickApply:   {backgroundColor:T.primary,borderRadius:10,paddingVertical:9,alignItems:'center',marginTop:4},
  quickApplyT:  {color:T.white,fontSize:13,fontWeight:'600'},
  saveJobBtn:   {backgroundColor:T.primaryBg,borderRadius:10,paddingHorizontal:10,justifyContent:'center',alignItems:'center',marginTop:4,borderWidth:1,borderColor:'#BFDBFE'},

  // Offer
  offerCard:    {backgroundColor:T.card,borderRadius:14,padding:16,marginBottom:10,marginHorizontal:16,...SHADOW_SM,borderWidth:1,borderColor:T.border},
  offerPos:     {fontSize:15,fontWeight:'700',color:T.text,flex:1,marginRight:8},
  offerBtn:     {flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',paddingVertical:10,borderRadius:10,gap:6},
  offerBtnT:    {color:T.white,fontSize:13,fontWeight:'600'},

  // Career insights
  funnelCard:   {backgroundColor:T.card,borderRadius:14,padding:14,marginBottom:12,borderWidth:1,borderColor:T.border,...SHADOW_SM},
  funnelTitle:  {fontSize:14,fontWeight:'700',color:T.text,marginBottom:12},
  metricCard:   {flex:1,backgroundColor:T.card,borderRadius:14,paddingVertical:14,paddingHorizontal:8,alignItems:'center',borderTopWidth:3,borderWidth:1,borderColor:T.border,...SHADOW_SM},
  metricVal:    {fontSize:22,fontWeight:'900',marginTop:8,marginBottom:3},
  metricLbl:    {fontSize:11,color:T.textSec,fontWeight:'600',textAlign:'center'},

  // AI
  aiCard:       {marginHorizontal:14,marginBottom:16,borderRadius:16,overflow:'hidden',...SHADOW},
  aiCardGrad:   {flexDirection:'row',alignItems:'center',padding:18},
  aiIconWrap:   {width:56,height:56,borderRadius:28,backgroundColor:'rgba(255,255,255,0.15)',justifyContent:'center',alignItems:'center',marginRight:14},
  aiTitle:      {fontSize:16,fontWeight:'700',color:T.white,marginBottom:3},
  aiSub:        {fontSize:12,color:'rgba(255,255,255,0.8)'},

  // Tiles
  tilesGrid:    {paddingHorizontal:TILE_PADDING,flexDirection:'row',flexWrap:'wrap',gap:TILE_GAP},
  tile:         {borderRadius:14,...SHADOW_SM,overflow:'hidden'},
  tileContent:  {alignItems:'center',paddingVertical:16,paddingHorizontal:8},
  tileIconWrap: {position:'relative',marginBottom:10},
  lockOverlay:  {position:'absolute',top:-4,right:-10,backgroundColor:'rgba(0,0,0,0.35)',borderRadius:8,padding:2},
  tileLabel:    {fontSize:12,fontWeight:'600',color:T.white,textAlign:'center',lineHeight:16},

  // Logout
  logoutBtn:    {backgroundColor:T.card,borderRadius:14,padding:16,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:10,borderWidth:1,borderColor:T.border,...SHADOW_SM},

  // Bottom nav
  bottomNav:    {flexDirection:'row',position:'absolute',bottom:0,left:0,right:0,backgroundColor:T.card,height:Platform.OS==='ios'?78:68,borderTopWidth:1,borderTopColor:T.border,paddingBottom:Platform.OS==='ios'?18:8,...SHADOW},
  navItem:      {flex:1,justifyContent:'center',alignItems:'center',paddingTop:8},
});
